import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import TrialExpired from '../components/TrialExpired';
import { useConfig } from '../context/ConfigContext';
import usePullToRefresh from '../hooks/usePullToRefresh';
import { formatPhoneDisplay } from '../utils/phoneFormat';
import { PRODUCT_TYPE } from '../config/product';

function ServiceMode() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, isAdmin, isDriver, isSuperAdmin, isTrialExpired, isPro, shouldHidePrices } = useAuth();
  const { getAppName } = useConfig();

  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(null);
  const [showPrintView, setShowPrintView] = useState(false);
  const printRef = useRef();

  // Route filter state (active/completed/archived)
  const [routeFilter, setRouteFilter] = useState('active');

  // Route sort order (newest/oldest) - persisted to localStorage
  const [routeSortOrder, setRouteSortOrder] = useState(() => {
    return localStorage.getItem('lawntrack_route_sort') || 'newest';
  });

  // Add customer mid-route state
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [availableCustomers, setAvailableCustomers] = useState([]);
  const [customerSearch, setCustomerSearch] = useState('');
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [reoptimizing, setReoptimizing] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [pendingPaymentStop, setPendingPaymentStop] = useState(null);
  const [isEditingPayment, setIsEditingPayment] = useState(false);

  // Discount state for payment modal
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  const [stopDiscount, setStopDiscount] = useState({ amount: '', type: '', reason: '', ongoing: false });
  const [companySeasonalDiscount, setCompanySeasonalDiscount] = useState({ percent: 0, amount: 0 });

  // Extra charge state for payment modal
  const [showExtraChargeForm, setShowExtraChargeForm] = useState(false);
  const [extraCharge, setExtraCharge] = useState({ amount: '', reason: '' });

  // Comp/free stop state
  const [isComp, setIsComp] = useState(false);
  const [compReason, setCompReason] = useState('');

  // Photo upload state
  const [stopPhotos, setStopPhotos] = useState([]);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const photoInputRef = useRef(null);

  // Service checklist state for lawn services (all checked by default except fertilizer)
  const [serviceChecklist, setServiceChecklist] = useState({
    mow: true,
    edge: true,
    trim: true,
    blow: true,
    fertilizer: false
  });

  // Service add-ons state
  const [availableAddons, setAvailableAddons] = useState([]);
  const [selectedAddons, setSelectedAddons] = useState({}); // { addonId: true/false }

  // Route expenses for summary report
  const [routeExpenses, setRouteExpenses] = useState([]);

  // Referral discount state - maps customer_id to discount info
  const [referralDiscounts, setReferralDiscounts] = useState({});

  // Phone action popup state
  const [phonePopup, setPhonePopup] = useState({ show: false, phone: '', x: 0, y: 0 });
  const phonePopupRef = useRef(null);

  // Billing modal state
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [billingInProgress, setBillingInProgress] = useState(false);
  const [billingResults, setBillingResults] = useState(null);

  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState([
    { id: 'square', name: 'Square', enabled: true, isBuiltIn: true },
    { id: 'venmo', name: 'Venmo', enabled: true, isBuiltIn: true },
    { id: 'cash', name: 'Cash', enabled: true, isBuiltIn: true },
    { id: 'cashapp', name: 'Cash App', enabled: true, isBuiltIn: true },
    { id: 'zelle', name: 'Zelle', enabled: true, isBuiltIn: true },
    { id: 'check', name: 'Check', enabled: true, isBuiltIn: true },
  ]);

  // Lead sources state
  const [leadSources, setLeadSources] = useState([
    { id: 'facebook', name: 'Facebook', enabled: true, isBuiltIn: true },
    { id: 'craigslist', name: 'Craigslist', enabled: true, isBuiltIn: true },
    { id: 'flyer', name: 'Flyer', enabled: true, isBuiltIn: true },
    { id: 'yard_sign', name: 'Yard Sign', enabled: true, isBuiltIn: true },
    { id: 'referral', name: 'Friend/Referral', enabled: true, isBuiltIn: true },
    { id: 'google', name: 'Google', enabled: true, isBuiltIn: true },
    { id: 'nextdoor', name: 'Nextdoor', enabled: true, isBuiltIn: true },
    { id: 'other', name: 'Other', enabled: true, isBuiltIn: true },
  ]);

  // Pricing tiers state
  const [pricingTiers, setPricingTiers] = useState([]);

  // Quick-add new customer state
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    firstName: '',
    lastName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
    tier: 'small',
    priceSnow: '50.00',
    saltPrice: '8.00',
    driveway: true,
    sidewalks: false,
    walkways: false,
    salt: false,
    paymentMethodPreferred: 'square',
    serviceFrequency: 'seasonal',
    notes: '',
    leadSource: ''
  });

  // GPS optimization state
  const [useGpsForOptimization, setUseGpsForOptimization] = useState(true);
  const [gpsStatus, setGpsStatus] = useState(''); // '', 'acquiring', 'success', 'unavailable'

  // Start route modal state
  const [showStartRouteModal, setShowStartRouteModal] = useState(false);
  const [pendingRouteToStart, setPendingRouteToStart] = useState(null);
  const [startLocationOption, setStartLocationOption] = useState('gps'); // 'gps' or 'saved'
  const [gpsStartLocation, setGpsStartLocation] = useState(null); // { lat, lon, address }
  const [gpsStartStatus, setGpsStartStatus] = useState(''); // '', 'detecting', 'success', 'error'
  const [startingRoute, setStartingRoute] = useState(false);

  const loadRoutes = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/routes?product=${PRODUCT_TYPE}`);
      setRoutes(response.data);
    } catch (err) {
      console.error('Failed to load routes:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load pricing tiers
  const loadPricingTiers = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/pricing-tiers?product=${PRODUCT_TYPE}`);
      setPricingTiers(response.data);
      // Update default new customer tier and price based on first tier
      if (response.data.length > 0) {
        const firstTier = response.data[0];
        setNewCustomer(prev => ({
          ...prev,
          tier: String(firstTier.id),
          priceSnow: parseFloat(firstTier.default_price).toFixed(2),
          saltPrice: parseFloat(firstTier.fertilizer_price || firstTier.salt_price || 0).toFixed(2),
        }));
      }
    } catch (err) {
      console.error('Failed to load pricing tiers:', err);
    }
  }, []);

  // Load payment methods from settings
  const loadPaymentMethods = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings/payment-methods`);
      if (response.data && response.data.length > 0) {
        setPaymentMethods(response.data);
      }
    } catch (err) {
      console.error('Failed to load payment methods:', err);
    }
  }, []);

  const loadLeadSources = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings/lead-sources`);
      if (response.data && response.data.length > 0) {
        setLeadSources(response.data);
      }
    } catch (err) {
      console.error('Failed to load lead sources:', err);
    }
  }, []);

  // Load company settings for GPS optimization preference and seasonal discount
  const loadCompanySettings = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/company?product=${PRODUCT_TYPE}`);
      if (response.data) {
        setUseGpsForOptimization(response.data.route_optimize_use_gps ?? true);
        setCompanySeasonalDiscount({
          percent: parseFloat(response.data.seasonal_discount_percent) || 0,
          amount: parseFloat(response.data.seasonal_discount_amount) || 0,
        });
      }
    } catch (err) {
      console.error('Failed to load company settings:', err);
    }
  }, []);

  // Load service add-ons for lawn services
  const loadServiceAddons = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/service-addons?product=lawn`);
      if (response.data && response.data.length > 0) {
        // Only show active add-ons
        setAvailableAddons(response.data.filter(a => a.is_active));
      }
    } catch (err) {
      console.error('Failed to load service add-ons:', err);
    }
  }, []);

  // Get only enabled payment methods and lead sources
  const enabledPaymentMethods = paymentMethods.filter(m => m.enabled);
  const enabledLeadSources = leadSources.filter(s => s.enabled);

  // Load referral discounts for all customers in a route
  const loadReferralDiscounts = useCallback(async (routeData) => {
    if (!routeData?.stops || !routeData.storm_id) {
      setReferralDiscounts({});
      return;
    }

    try {
      const discounts = {};
      // Fetch discounts for ALL stops (not just pending) so print summary has accurate data
      const allStops = routeData.stops.filter(s => s.status !== 'skipped');

      await Promise.all(
        allStops.map(async (stop) => {
          try {
            const url = `${process.env.REACT_APP_API_URL}/api/referral-discount/${stop.customer_id}?stormId=${encodeURIComponent(routeData.storm_id)}`;

            const response = await axios.get(url);
            discounts[stop.customer_id] = response.data;
          } catch (err) {
            console.error(`Failed to load discount for customer ${stop.customer_id}:`, err);
          }
        })
      );

      setReferralDiscounts(discounts);
    } catch (err) {
      console.error('[Referral Debug] Failed to load referral discounts:', err);
    }
  }, []);

  // Reverse geocode coordinates to address
  const reverseGeocode = async (lat, lon) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/weather/reverse-geocode?lat=${lat}&lon=${lon}`
      );
      return response.data.address || `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
    } catch (err) {
      console.error('Reverse geocode error:', err);
      return `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
    }
  };

  // Detect GPS location for route start
  const detectGpsForStart = useCallback(async () => {
    setGpsStartStatus('detecting');
    setGpsStartLocation(null);

    if (!navigator.geolocation) {
      setGpsStartStatus('error');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;
        const address = await reverseGeocode(lat, lon);
        setGpsStartLocation({ lat, lon, address });
        setGpsStartStatus('success');
      },
      (error) => {
        console.error('GPS error:', error);
        setGpsStartStatus('error');
        setStartLocationOption('saved'); // Fall back to saved location
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 }
    );
  }, []);

  // Select and load a route
  const selectRoute = useCallback(async (route) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${route.id}`
      );
      setSelectedRoute(response.data);

      // Load expenses for this route
      try {
        const expensesResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/expenses?routeId=${route.id}`
        );
        // Backend now filters by routeId, response is { expenses: [...] }
        setRouteExpenses(expensesResponse.data.expenses || []);
      } catch (expErr) {
        // Non-pro users or other errors - just skip expenses
        setRouteExpenses([]);
      }

      // Load referral discounts if route has storm_id
      if (response.data.storm_id) {
        await loadReferralDiscounts(response.data);
      } else {
        setReferralDiscounts({});
      }
    } catch (err) {
      console.error('Failed to load route details:', err);
    }
  }, [loadReferralDiscounts]);

  // Show start route modal for not-started routes
  const handleRouteClick = useCallback(async (route) => {
    if (route.status === 'not_started') {
      // Load full route details first
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/routes/${route.id}`
        );
        setPendingRouteToStart(response.data);
        setShowStartRouteModal(true);
        setStartLocationOption(useGpsForOptimization ? 'gps' : 'saved');
        setGpsStartLocation(null);
        setGpsStartStatus('');

        // Auto-detect GPS if preference is enabled
        if (useGpsForOptimization) {
          detectGpsForStart();
        }
      } catch (err) {
        console.error('Failed to load route for start modal:', err);
      }
    } else {
      // Route already started, just select it
      selectRoute(route);
    }
  }, [useGpsForOptimization, detectGpsForStart, selectRoute]);

  // Confirm and start route from modal
  const confirmStartRoute = async () => {
    if (!pendingRouteToStart) return;

    setStartingRoute(true);
    try {
      // Update route status and optionally starting location
      const updateData = {
        ...pendingRouteToStart,
        status: 'in_progress',
      };

      // If using GPS location, update starting address
      if (startLocationOption === 'gps' && gpsStartLocation) {
        updateData.starting_address = gpsStartLocation.address.split(',')[0]?.trim() || gpsStartLocation.address;
        updateData.starting_lat = gpsStartLocation.lat;
        updateData.starting_lon = gpsStartLocation.lon;
      }

      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/routes/${pendingRouteToStart.id}`,
        updateData
      );

      // Close modal and select route
      setShowStartRouteModal(false);
      setPendingRouteToStart(null);
      await selectRoute(pendingRouteToStart);
    } catch (err) {
      console.error('Failed to start route:', err);
      alert('Failed to start route. Please try again.');
    } finally {
      setStartingRoute(false);
    }
  };

  // Refresh function for pull-to-refresh - reloads routes and selected route
  const handleRefresh = useCallback(async () => {
    await loadRoutes();
    if (selectedRoute) {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
        );
        setSelectedRoute(response.data);
      } catch (err) {
        console.error('Failed to refresh selected route:', err);
      }
    }
  }, [loadRoutes, selectedRoute]);

  const { PullToRefreshIndicator } = usePullToRefresh(handleRefresh);

  useEffect(() => {
    loadRoutes();
    loadPricingTiers();
    loadPaymentMethods();
    loadLeadSources();
    loadCompanySettings();
    loadServiceAddons();
  }, [loadRoutes, loadPricingTiers, loadPaymentMethods, loadLeadSources, loadCompanySettings, loadServiceAddons]);

  // If we came here with a specific routeId, load that route
  useEffect(() => {
    if (location.state?.routeId && routes.length > 0) {
      const route = routes.find(r => r.id === location.state.routeId);
      if (route) {
        selectRoute(route);
      }
    }
  }, [location.state, routes, selectRoute]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (showAddCustomer) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showAddCustomer]);

  // Close phone popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (phonePopup.show && phonePopupRef.current && !phonePopupRef.current.contains(e.target)) {
        setPhonePopup({ show: false, phone: '', x: 0, y: 0 });
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [phonePopup.show]);

  // Handle phone click - show call/text popup
  const handlePhoneClick = (e, phone) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.target.getBoundingClientRect();
    setPhonePopup({
      show: true,
      phone: phone,
      x: rect.left,
      y: rect.bottom + 4
    });
  };

  // Toggle route sort order and save to localStorage
  const toggleRouteSortOrder = () => {
    const newOrder = routeSortOrder === 'newest' ? 'oldest' : 'newest';
    setRouteSortOrder(newOrder);
    localStorage.setItem('lawntrack_route_sort', newOrder);
  };

  // Categorize stops for billing
  const getBillingCategories = () => {
    const completedStops = selectedRoute?.stops?.filter(s => s.status === 'completed') || [];

    const categories = {
      autoBill: [], // Square + email + unpaid + no invoice
      noEmail: [], // Square + no email
      manual: [], // Non-Square payment methods
    };

    completedStops.forEach(stop => {
      // Skip if already paid or invoice already sent
      if (stop.payment_status === 'paid' || stop.invoice_sent_at) return;

      const isSquare = stop.payment_method?.toLowerCase().includes('square');
      const hasEmail = !!stop.email;
      const amount = getEffectivePrice(stop);
      const customerName = `${stop.first_name || ''} ${stop.last_name || ''}`.trim();

      if (isSquare && hasEmail) {
        categories.autoBill.push({ ...stop, customerName, amount });
      } else if (isSquare && !hasEmail) {
        categories.noEmail.push({ ...stop, customerName, amount });
      } else if (stop.payment_method) {
        categories.manual.push({ ...stop, customerName, amount });
      }
    });

    return categories;
  };

  // Send invoices to all Square customers
  const handleBillAll = async () => {
    if (!selectedRoute?.id) return;

    setBillingInProgress(true);
    setBillingResults(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/bill-all`
      );

      setBillingResults(response.data);

      // Refresh the route to get updated invoice_sent_at
      const routeResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
      );
      setSelectedRoute(routeResponse.data);
    } catch (error) {
      console.error('Billing error:', error);
      setBillingResults({
        sent: [],
        failed: [{ name: 'Error', reason: error.response?.data?.error || 'Failed to send invoices' }]
      });
    } finally {
      setBillingInProgress(false);
    }
  };

  // Show payment modal when Done is clicked
  const markStopComplete = (stop) => {
    // Check if trial expired before allowing stop completion
    if (isTrialExpired()) {
      setShowTrialModal(true);
      return;
    }
    setPendingPaymentStop(stop);
    setIsEditingPayment(false);
    // Auto-populate seasonal discount if customer is seasonal
    if (stop.service_preference === 'seasonal') {
      if (companySeasonalDiscount.percent > 0) {
        setStopDiscount({
          amount: companySeasonalDiscount.percent.toString(),
          type: 'percent',
          reason: 'Seasonal contract discount',
          ongoing: false
        });
        setShowDiscountForm(true);
      } else if (companySeasonalDiscount.amount > 0) {
        setStopDiscount({
          amount: companySeasonalDiscount.amount.toString(),
          type: 'dollar',
          reason: 'Seasonal contract discount',
          ongoing: false
        });
        setShowDiscountForm(true);
      } else {
        setShowDiscountForm(false);
        setStopDiscount({ amount: '', type: '', reason: '', ongoing: false });
      }
    } else {
      setShowDiscountForm(false);
      setStopDiscount({ amount: '', type: '', reason: '', ongoing: false });
    }
    // Reset extra charge form
    setShowExtraChargeForm(false);
    setExtraCharge({ amount: '', reason: '' });
    // Reset service checklist to defaults (all checked except fertilizer)
    setServiceChecklist({
      mow: true,
      edge: true,
      trim: true,
      blow: true,
      fertilizer: false
    });
    // Reset selected add-ons
    setSelectedAddons({});
    setShowPaymentModal(true);
    loadStopPhotos(stop.id);
  };

  // Open payment modal to edit existing payment status
  const editPaymentStatus = (stop) => {
    setPendingPaymentStop(stop);
    setIsEditingPayment(true);

    // Pre-populate discount if exists, otherwise reset
    if (stop.discount_amount && stop.discount_type) {
      setStopDiscount({
        amount: stop.discount_amount.toString(),
        type: stop.discount_type,
        reason: stop.discount_reason || '',
        ongoing: false
      });
      setShowDiscountForm(true);
    } else {
      setStopDiscount({ amount: '', type: '', reason: '', ongoing: false });
      setShowDiscountForm(false);
    }

    // Pre-populate extra charge if exists, otherwise reset
    if (stop.extra_charge_amount && parseFloat(stop.extra_charge_amount) > 0) {
      setExtraCharge({
        amount: stop.extra_charge_amount.toString(),
        reason: stop.extra_charge_reason || ''
      });
      setShowExtraChargeForm(true);
    } else {
      setExtraCharge({ amount: '', reason: '' });
      setShowExtraChargeForm(false);
    }

    // Pre-populate service checklist from existing stop data
    setServiceChecklist({
      mow: stop.service_mow !== false,  // Default to true if null
      edge: stop.service_edge !== false,
      trim: stop.service_trim !== false,
      blow: stop.service_blow !== false,
      fertilizer: stop.service_fertilizer === true  // Default to false if null
    });

    // Pre-populate selected add-ons from existing stop data
    if (stop.addons && Array.isArray(stop.addons)) {
      const addonsMap = {};
      stop.addons.forEach(addon => {
        addonsMap[addon.addon_id] = true;
      });
      setSelectedAddons(addonsMap);
    } else {
      setSelectedAddons({});
    }

    // Pre-populate comp status if exists
    setIsComp(stop.is_comp === true);
    setCompReason(stop.comp_reason || '');

    setShowPaymentModal(true);
    loadStopPhotos(stop.id);
  };

  // Load photos for a stop
  const loadStopPhotos = async (stopId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/route-stops/${stopId}/photos`);
      setStopPhotos(response.data);
    } catch (err) {
      console.error('Failed to load photos:', err);
      setStopPhotos([]);
    }
  };

  // Handle photo upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !pendingPaymentStop) return;

    // Compress image before upload
    const compressedFile = await compressImage(file);

    setPhotoUploading(true);
    try {
      const formData = new FormData();
      formData.append('photo', compressedFile);
      formData.append('photoType', 'other');

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/route-stops/${pendingPaymentStop.id}/photos`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setStopPhotos(prev => [response.data, ...prev]);
    } catch (err) {
      console.error('Photo upload failed:', err);
      alert('Failed to upload photo. Please try again.');
    } finally {
      setPhotoUploading(false);
      if (photoInputRef.current) {
        photoInputRef.current.value = '';
      }
    }
  };

  // Compress image before upload
  const compressImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxSize = 1200;
          let width = img.width;
          let height = img.height;

          if (width > height && width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          } else if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob((blob) => {
            resolve(new File([blob], file.name, { type: 'image/jpeg' }));
          }, 'image/jpeg', 0.8);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  // Delete a photo
  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/photos/${photoId}`);
      setStopPhotos(prev => prev.filter(p => p.id !== photoId));
    } catch (err) {
      console.error('Failed to delete photo:', err);
      alert('Failed to delete photo.');
    }
  };

  // Handle saving just adjustments without changing payment method (for editing completed stops)
  const handleSaveAdjustments = async () => {
    if (!pendingPaymentStop) return;

    const stop = pendingPaymentStop;
    setShowPaymentModal(false);
    setStopPhotos([]);
    setSelectedPhoto(null);
    setCompleting(stop.id);

    try {
      const editPayload = {};

      // Handle discount - can be added, updated, or cleared
      if (stopDiscount.type && stopDiscount.amount) {
        editPayload.discountAmount = parseFloat(stopDiscount.amount);
        editPayload.discountType = stopDiscount.type;
        editPayload.discountReason = stopDiscount.reason || null;
      } else {
        // Clear discount if form is empty
        editPayload.discountAmount = null;
        editPayload.discountType = null;
        editPayload.discountReason = null;
      }

      // Handle extra charge - can be added, updated, or cleared
      if (extraCharge.amount && parseFloat(extraCharge.amount) > 0) {
        editPayload.extraChargeAmount = parseFloat(extraCharge.amount);
        editPayload.extraChargeReason = extraCharge.reason || null;
      } else {
        // Clear extra charge if form is empty
        editPayload.extraChargeAmount = null;
        editPayload.extraChargeReason = null;
      }

      // Handle comp status - can be added, updated, or cleared
      editPayload.isComp = isComp;
      editPayload.compReason = isComp ? (compReason || null) : null;

      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops/${stop.id}`,
        editPayload
      );

      // Reload route
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
      );
      setSelectedRoute(response.data);

      // Reload referral discounts
      if (response.data.storm_id) {
        await loadReferralDiscounts(response.data);
      }
    } catch (err) {
      console.error('Save adjustments error:', err);
      alert('Failed to save changes');
    } finally {
      setCompleting(null);
      setPendingPaymentStop(null);
      setIsEditingPayment(false);
      setShowDiscountForm(false);
      setShowExtraChargeForm(false);
      setExtraCharge({ amount: '', reason: '' });
      setIsComp(false);
      setCompReason('');
    }
  };

  // Handle payment selection and complete the stop (or update existing payment)
  // paymentOption format: 'method_status' (e.g., 'square_paid', 'venmo_not_paid', 'not_paid')
  const handlePaymentSelection = async (paymentOption) => {
    if (!pendingPaymentStop) return;

    const stop = pendingPaymentStop;

    // Parse the payment option to get method and status
    let paymentStatus;
    let actualPaymentMethod = null;

    if (paymentOption === 'comp') {
      paymentStatus = 'paid'; // Comp is considered "paid" (no balance due)
      actualPaymentMethod = 'comp';
    } else if (paymentOption === 'not_paid') {
      paymentStatus = 'not_paid';
      actualPaymentMethod = null;
    } else if (paymentOption === 'square') {
      paymentStatus = 'unpaid'; // Square customers get invoiced later via Bill All
      actualPaymentMethod = 'square';
    } else if (paymentOption === 'venmo_paid') {
      paymentStatus = 'paid';
      actualPaymentMethod = 'venmo';
    } else if (paymentOption === 'venmo_not_paid') {
      paymentStatus = 'not_paid';
      actualPaymentMethod = 'venmo';
    } else if (paymentOption === 'cashapp_paid') {
      paymentStatus = 'paid';
      actualPaymentMethod = 'cashapp';
    } else if (paymentOption === 'cashapp_not_paid') {
      paymentStatus = 'not_paid';
      actualPaymentMethod = 'cashapp';
    } else if (paymentOption === 'cash_paid') {
      paymentStatus = 'paid';
      actualPaymentMethod = 'cash';
    } else if (paymentOption === 'zelle_paid') {
      paymentStatus = 'paid';
      actualPaymentMethod = 'zelle';
    } else if (paymentOption === 'zelle_not_paid') {
      paymentStatus = 'not_paid';
      actualPaymentMethod = 'zelle';
    } else if (paymentOption === 'check_paid') {
      paymentStatus = 'paid';
      actualPaymentMethod = 'check';
    } else if (paymentOption === 'check_not_paid') {
      paymentStatus = 'not_paid';
      actualPaymentMethod = 'check';
    } else {
      // Fallback for legacy calls
      paymentStatus = 'paid';
      actualPaymentMethod = paymentOption;
    }

    setShowPaymentModal(false);
    setStopPhotos([]);
    setSelectedPhoto(null);
    setCompleting(stop.id);

    try {
      if (isEditingPayment) {
        // Update payment status, discount, extra charge, and salt for completed stop
        const editPayload = {
          paymentStatus: paymentStatus,
          paymentMethod: paymentOption !== 'not_paid' ? actualPaymentMethod : null,
        };

        // Handle discount - can be added, updated, or cleared
        if (stopDiscount.type && stopDiscount.amount) {
          editPayload.discountAmount = parseFloat(stopDiscount.amount);
          editPayload.discountType = stopDiscount.type;
          editPayload.discountReason = stopDiscount.reason || null;
        } else {
          // Clear discount if form is empty
          editPayload.discountAmount = null;
          editPayload.discountType = null;
          editPayload.discountReason = null;
        }

        // Handle extra charge - can be added, updated, or cleared
        if (extraCharge.amount && parseFloat(extraCharge.amount) > 0) {
          editPayload.extraChargeAmount = parseFloat(extraCharge.amount);
          editPayload.extraChargeReason = extraCharge.reason || null;
        } else {
          // Clear extra charge if form is empty
          editPayload.extraChargeAmount = null;
          editPayload.extraChargeReason = null;
        }

        // Add service checklist fields
        editPayload.serviceMow = serviceChecklist.mow;
        editPayload.serviceEdge = serviceChecklist.edge;
        editPayload.serviceTrim = serviceChecklist.trim;
        editPayload.serviceBlow = serviceChecklist.blow;
        editPayload.serviceFertilizer = serviceChecklist.fertilizer;

        // Handle comp status - can be added, updated, or cleared
        editPayload.isComp = isComp;
        editPayload.compReason = isComp ? (compReason || null) : null;

        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops/${stop.id}`,
          editPayload
        );
      } else {
        // Build the stop update payload with service checklist
        const stopUpdatePayload = {
          status: 'completed',
          completedAt: new Date().toISOString(),
          paymentStatus: paymentStatus,
          paymentMethod: paymentOption !== 'not_paid' ? actualPaymentMethod : null,
          // Service checklist fields
          serviceMow: serviceChecklist.mow,
          serviceEdge: serviceChecklist.edge,
          serviceTrim: serviceChecklist.trim,
          serviceBlow: serviceChecklist.blow,
          serviceFertilizer: serviceChecklist.fertilizer,
        };

        // Add discount fields if a discount was applied
        if (stopDiscount.type && stopDiscount.amount) {
          stopUpdatePayload.discountAmount = parseFloat(stopDiscount.amount);
          stopUpdatePayload.discountType = stopDiscount.type;
          stopUpdatePayload.discountReason = stopDiscount.reason || null;
          stopUpdatePayload.applyOngoing = stopDiscount.ongoing;
        }

        // Add extra charge fields if an extra charge was applied
        if (extraCharge.amount && parseFloat(extraCharge.amount) > 0) {
          stopUpdatePayload.extraChargeAmount = parseFloat(extraCharge.amount);
          stopUpdatePayload.extraChargeReason = extraCharge.reason || null;
        }

        // Add comp fields if marked as comp
        if (isComp) {
          stopUpdatePayload.isComp = true;
          stopUpdatePayload.compReason = compReason || null;
        }

        // Add selected service add-ons
        const selectedAddonsList = availableAddons
          .filter(addon => selectedAddons[addon.id])
          .map(addon => {
            const customerTierId = stop?.lawn_tier_id || stop?.pricing_tier_id;
            const tierPrice = customerTierId && addon.tier_prices
              ? addon.tier_prices.find(tp => tp.pricing_tier_id === customerTierId)?.price
              : undefined;
            return { addonId: addon.id, priceCharged: tierPrice !== undefined ? tierPrice : parseFloat(addon.price) || 0 };
          });
        if (selectedAddonsList.length > 0) {
          stopUpdatePayload.addons = selectedAddonsList;
        }

        // New completion - set status and log service
        const stopUpdateResponse = await axios.put(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops/${stop.id}`,
          stopUpdatePayload
        );

        // Check for auto-invoice result and show notification
        if (stopUpdateResponse.data?.autoInvoice) {
          const autoInvoice = stopUpdateResponse.data.autoInvoice;
          if (autoInvoice.success) {
            // Use setTimeout to show alert after modal closes
            setTimeout(() => {
              alert(`✓ Invoice sent to ${autoInvoice.customerName}`);
            }, 100);
          } else if (autoInvoice.error) {
            setTimeout(() => {
              alert(`⚠ Auto-invoice failed: ${autoInvoice.error}`);
            }, 100);
          }
        }

        // Get calculated price (with extra charge and add-ons if applicable)
        // Comp stops are logged with $0 price
        const basePrice = isComp ? 0 : (parseFloat(stop.price_snow) || 35.00);
        const extraChargeAmt = isComp ? 0 : ((extraCharge.amount && parseFloat(extraCharge.amount) > 0) ? parseFloat(extraCharge.amount) : 0);
        // Calculate add-ons total
        const addonsTotal = isComp ? 0 : selectedAddonsList.reduce((sum, a) => sum + a.priceCharged, 0);
        let calculatedPrice = basePrice + extraChargeAmt + addonsTotal;
        if (!isComp && stopDiscount.type && stopDiscount.amount) {
          calculatedPrice = calculateDiscountedPrice(basePrice + extraChargeAmt + addonsTotal, parseFloat(stopDiscount.amount), stopDiscount.type);
        }

        // Log the service (only for new completions)
        // Price includes base, extra charge, and add-ons
        await axios.post(`${process.env.REACT_APP_API_URL}/api/services`, {
          customerId: stop.customer_id,
          routeId: selectedRoute.id,
          routeStopId: stop.id,
          serviceType: selectedRoute.service_type || 'lawn_care',
          serviceDate: new Date().toISOString().split('T')[0],
          price: basePrice + extraChargeAmt + addonsTotal,
          calculatedPrice: calculatedPrice,
        });
      }

      // Reload route
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
      );
      setSelectedRoute(response.data);

      // Reload referral discounts after completion (other customers may now have more completed referrals)
      if (!isEditingPayment && response.data.storm_id) {
        await loadReferralDiscounts(response.data);
      }

      // Check if all stops are completed (only relevant for new completions)
      if (!isEditingPayment) {
        const allCompleted = response.data.stops?.every(s => s.status === 'completed' || s.status === 'skipped');
        if (allCompleted && response.data.stops?.length > 0) {
          await axios.put(`${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`, {
            ...response.data,
            status: 'completed',
          });
          loadRoutes();
        }
      }
    } catch (err) {
      console.error('Failed to update stop:', err);
      alert(isEditingPayment ? 'Failed to update payment status' : 'Failed to mark stop as complete');
    } finally {
      setCompleting(null);
      setPendingPaymentStop(null);
      setIsEditingPayment(false);
      // Reset discount and extra charge forms
      setShowDiscountForm(false);
      setStopDiscount({ amount: '', type: '', reason: '', ongoing: false });
      setShowExtraChargeForm(false);
      setExtraCharge({ amount: '', reason: '' });
      // Reset comp status
      setIsComp(false);
      setCompReason('');
    }
  };

  const markStopSkipped = async (stop) => {
    setCompleting(stop.id);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops/${stop.id}`,
        {
          status: 'skipped',
          completedAt: new Date().toISOString(),
          notes: 'Skipped',
        }
      );

      // Recalculate referral discount for the referrer (if this customer was referred)
      // This adjusts the referrer's price since this referral is now skipped
      if (selectedRoute.storm_id) {
        try {
          await axios.post(
            `${process.env.REACT_APP_API_URL}/api/referral-discount/recalculate-after-skip`,
            {
              skippedCustomerId: stop.customer_id,
              stormId: selectedRoute.storm_id,
              routeId: selectedRoute.id
            }
          );
        } catch (recalcErr) {
          console.error('[Referral Recalc] Failed to recalculate:', recalcErr);
          // Don't block the skip operation if recalculation fails
        }
      }

      // Reload route
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
      );
      setSelectedRoute(response.data);

      // Reload referral discounts to reflect the skip
      if (response.data.storm_id) {
        await loadReferralDiscounts(response.data);
      }
    } catch (err) {
      console.error('Failed to skip stop:', err);
      alert('Failed to skip stop');
    } finally {
      setCompleting(null);
    }
  };

  const resetRoute = async () => {
    if (!window.confirm('Reset all stops to pending? This will clear completion status.')) {
      return;
    }

    try {
      // Reset all stops
      for (const stop of selectedRoute.stops) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops/${stop.id}`,
          {
            status: 'pending',
            completedAt: null,
          }
        );
      }

      // Reset route status
      await axios.put(`${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`, {
        ...selectedRoute,
        status: 'not_started',
      });

      // Reload
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
      );
      setSelectedRoute(response.data);
      loadRoutes();
      alert('Route reset - all stops set to pending');
    } catch (err) {
      console.error('Failed to reset route:', err);
      alert('Failed to reset route');
    }
  };

  // Archive or unarchive a route
  const toggleArchiveRoute = async () => {
    const isArchived = selectedRoute.is_archived;
    const action = isArchived ? 'unarchive' : 'archive';

    if (!window.confirm(`${isArchived ? 'Unarchive' : 'Archive'} this route?`)) {
      return;
    }

    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`, {
        ...selectedRoute,
        is_archived: !isArchived,
      });

      setSelectedRoute(null);
      loadRoutes();
      alert(`Route ${action}d successfully`);
    } catch (err) {
      console.error(`Failed to ${action} route:`, err);
      alert(`Failed to ${action} route`);
    }
  };

  const getProgress = () => {
    if (!selectedRoute?.stops?.length) return { completed: 0, total: 0, percentage: 0 };
    const completed = selectedRoute.stops.filter(s => s.status === 'completed' || s.status === 'skipped').length;
    const total = selectedRoute.stops.length;
    return {
      completed,
      total,
      percentage: Math.round((completed / total) * 100),
    };
  };

  const getPendingStops = () => {
    return selectedRoute?.stops?.filter(s => s.status === 'pending') || [];
  };

  const getCompletedStops = () => {
    return selectedRoute?.stops?.filter(s => s.status === 'completed' || s.status === 'skipped') || [];
  };

  const getServicesText = (stop) => {
    const services = [];
    if (stop.driveway) services.push('Mowing');
    if (stop.sidewalks) services.push('Edging');
    if (stop.walkways) services.push('Trimming');
    if (stop.salt) services.push('Fertilizer');
    return services.length > 0 ? services.join(', ') : 'Standard service';
  };

  // Helper to get payment method display name
  const getPaymentMethodName = (method) => {
    const methodNames = {
      'square': 'Square',
      'venmo': 'Venmo',
      'cashapp': 'Cash App',
      'cash': 'Cash',
      'zelle': 'Zelle',
      'check': 'Check'
    };
    return methodNames[method] || method || '';
  };

  // Helper to get combined payment status label (e.g., "Venmo - Not Paid", "Square - Invoice Sent")
  const getPaymentLabel = (stop) => {
    const methodName = getPaymentMethodName(stop.payment_method);
    if (stop.payment_status === 'paid') {
      return methodName ? `${methodName} - Paid` : 'Paid';
    } else if (stop.payment_status === 'invoice_sent') {
      // Square invoice sent - keep special terminology
      return methodName ? `${methodName} - Invoice Sent` : 'Invoice Sent';
    } else if (stop.payment_method && stop.payment_status !== 'paid') {
      // Other methods with not_paid status
      return `${methodName} - Not Paid`;
    }
    return 'Not Paid';
  };

  // Helper to calculate discounted price
  const calculateDiscountedPrice = (price, discountAmount, discountType) => {
    if (!discountAmount || discountAmount <= 0) return price;
    if (discountType === 'percent') {
      return Math.max(0, price - (price * discountAmount / 100));
    }
    return Math.max(0, price - discountAmount);
  };

  // Determine if salt should be included for a stop
  const shouldIncludeSalt = (stop) => {
    // If salt_applied is explicitly set on the stop, use that
    if (stop.salt_applied === true) return true;
    if (stop.salt_applied === false) return false;
    // Otherwise fall back to customer's default salt setting
    return stop.salt === true;
  };

  // Get effective price for a stop (considering extra charge, stop-level or customer-level discount)
  const getEffectivePrice = (stop) => {
    const includeSalt = shouldIncludeSalt(stop);
    const extraCharge = parseFloat(stop.extra_charge_amount || 0);
    const basePrice = parseFloat(stop.price_snow || 50) + (includeSalt ? parseFloat(stop.salt_price || 0) : 0) + extraCharge;
    // Check stop-level discount first
    if (stop.discount_amount && stop.discount_type) {
      return calculateDiscountedPrice(basePrice, parseFloat(stop.discount_amount), stop.discount_type);
    }
    // Then check customer-level discount
    if (stop.customer_discount_amount && stop.customer_discount_type) {
      return calculateDiscountedPrice(basePrice, parseFloat(stop.customer_discount_amount), stop.customer_discount_type);
    }
    return basePrice;
  };

  const calculateTotal = () => {
    const completedStops = selectedRoute?.stops?.filter(s => s.status === 'completed') || [];
    return completedStops.reduce((sum, stop) => {
      // Use effective price which handles discounts
      return sum + getEffectivePrice(stop);
    }, 0);
  };

  const calculatePaidTotal = () => {
    const paidStops = selectedRoute?.stops?.filter(s => s.status === 'completed' && s.payment_status === 'paid') || [];
    return paidStops.reduce((sum, stop) => sum + getEffectivePrice(stop), 0);
  };

  const calculateUnpaidTotal = () => {
    const unpaidStops = selectedRoute?.stops?.filter(s => s.status === 'completed' && s.payment_status !== 'paid') || [];
    return unpaidStops.reduce((sum, stop) => sum + getEffectivePrice(stop), 0);
  };

  const handlePrint = () => {
    setShowPrintView(true);
    setTimeout(() => {
      window.print();
    }, 100);
  };

  // Open Google Maps with directions to the stop address
  const openNavigation = (stop) => {
    const address = encodeURIComponent(
      `${stop.address}, ${stop.city}, ${stop.state} ${stop.zip}`
    );
    window.open(`https://www.google.com/maps/dir/?api=1&destination=${address}`, '_blank');
  };

  // Load available customers (not already on this route)
  const loadAvailableCustomers = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/customers?product=${PRODUCT_TYPE}`);
      // Filter out customers already on this route
      const currentCustomerIds = selectedRoute?.stops?.map(s => s.customer_id) || [];
      const available = response.data.filter(c => !currentCustomerIds.includes(c.id));
      setAvailableCustomers(available);
    } catch (err) {
      console.error('Failed to load customers:', err);
    }
  };

  // Open add customer modal
  const openAddCustomerModal = () => {
    loadAvailableCustomers();
    setCustomerSearch('');
    setShowAddCustomer(true);
  };

  // Add customer to route at optimal position
  const addCustomerMidRoute = async (customer) => {
    setAddingCustomer(true);
    try {
      // Find optimal insert position using Google Maps
      const insertResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/find-insert-position`,
        {
          customerId: customer.id
        }
      );

      const { insertPosition } = insertResponse.data;

      // Add the stop at the calculated position
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops`,
        {
          customerId: customer.id,
          stopOrder: insertPosition,
          notes: 'Added mid-route'
        }
      );

      // Shift stop orders for stops after the insertion point
      const pendingStops = selectedRoute.stops.filter(s => s.status === 'pending');
      for (const stop of pendingStops) {
        if (stop.stop_order >= insertPosition) {
          await axios.put(
            `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops/${stop.id}`,
            { stopOrder: stop.stop_order + 1 }
          );
        }
      }

      // Reload route
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
      );
      setSelectedRoute(response.data);
      setShowAddCustomer(false);
      alert(`${customer.first_name} ${customer.last_name} added to route at position ${insertPosition}!`);
    } catch (err) {
      console.error('Failed to add customer:', err);
      // Fallback: add to end of route
      try {
        const maxOrder = Math.max(...(selectedRoute.stops?.map(s => s.stop_order) || [0]), 0);
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops`,
          {
            customerId: customer.id,
            stopOrder: maxOrder + 1,
            notes: 'Added mid-route'
          }
        );
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
        );
        setSelectedRoute(response.data);
        setShowAddCustomer(false);
        alert(`${customer.first_name} ${customer.last_name} added to end of route.`);
      } catch (fallbackErr) {
        alert('Failed to add customer to route');
      }
    } finally {
      setAddingCustomer(false);
    }
  };

  // Handle tier change - auto-fill prices from dynamic pricing tiers
  const handleTierChange = (tier) => {
    let newPriceSnow = newCustomer.priceSnow;
    let newSaltPrice = newCustomer.saltPrice;

    if (tier !== 'custom') {
      // Find the selected tier from pricingTiers
      const selectedTier = pricingTiers.find(t => String(t.id) === tier);
      if (selectedTier) {
        newPriceSnow = parseFloat(selectedTier.default_price).toFixed(2);
        newSaltPrice = parseFloat(selectedTier.salt_price || 0).toFixed(2);
      }
    }
    // For 'custom', keep the current prices

    setNewCustomer({
      ...newCustomer,
      tier,
      priceSnow: newPriceSnow,
      saltPrice: newSaltPrice,
    });
  };

  // Quick-add new customer and add to route
  const createAndAddCustomer = async () => {
    // Validate required fields
    if (!newCustomer.firstName || !newCustomer.address || !newCustomer.city || !newCustomer.state || !newCustomer.zip) {
      alert('Please fill in Name, Address, City, State, and Zip');
      return;
    }

    setAddingCustomer(true);
    try {
      // Create the customer
      const customerResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/customers`,
        {
          firstName: newCustomer.firstName,
          lastName: newCustomer.lastName || '',
          address: newCustomer.address,
          city: newCustomer.city,
          state: newCustomer.state,
          zip: newCustomer.zip,
          phone: newCustomer.phone || '',
          email: newCustomer.email || '',
          tier: newCustomer.tier,
          priceSnow: parseFloat(newCustomer.priceSnow) || 50.00,
          saltPrice: parseFloat(newCustomer.saltPrice) || 8.00,
          driveway: newCustomer.driveway,
          sidewalks: newCustomer.sidewalks,
          walkways: newCustomer.walkways,
          salt: newCustomer.salt,
          paymentMethodPreferred: newCustomer.paymentMethodPreferred,
          serviceFrequency: newCustomer.serviceFrequency,
          notes: newCustomer.notes || '',
          leadSource: newCustomer.leadSource || null,
          serviceType: 'lawn_care',
          productType: PRODUCT_TYPE,
          isActive: true
        }
      );

      const createdCustomer = customerResponse.data;

      // Now add to route using the same logic as addCustomerMidRoute
      try {
        const insertResponse = await axios.post(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/find-insert-position`,
          { customerId: createdCustomer.id }
        );

        const { insertPosition } = insertResponse.data;

        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops`,
          {
            customerId: createdCustomer.id,
            stopOrder: insertPosition,
            notes: 'Service call - new customer'
          }
        );

        // Shift stop orders for stops after the insertion point
        const pendingStops = selectedRoute.stops.filter(s => s.status === 'pending');
        for (const stop of pendingStops) {
          if (stop.stop_order >= insertPosition) {
            await axios.put(
              `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops/${stop.id}`,
              { stopOrder: stop.stop_order + 1 }
            );
          }
        }

        // Reload route
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
        );
        setSelectedRoute(response.data);
        setShowAddCustomer(false);
        setShowNewCustomerForm(false);
        setNewCustomer({
          firstName: '', lastName: '', address: '', city: '', state: '', zip: '', phone: '', email: '',
          priceSnow: '35.00', driveway: true, sidewalks: false, walkways: false, salt: false,
          paymentMethodPreferred: 'square', serviceFrequency: 'seasonal', notes: ''
        });
        alert(`New customer ${createdCustomer.first_name} ${createdCustomer.last_name} created and added at position ${insertPosition}!`);
      } catch (routeErr) {
        // Fallback: add to end of route
        const maxOrder = Math.max(...(selectedRoute.stops?.map(s => s.stop_order) || [0]), 0);
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/stops`,
          {
            customerId: createdCustomer.id,
            stopOrder: maxOrder + 1,
            notes: 'Service call - new customer'
          }
        );
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
        );
        setSelectedRoute(response.data);
        setShowAddCustomer(false);
        setShowNewCustomerForm(false);
        setNewCustomer({
          firstName: '', lastName: '', address: '', city: '', state: '', zip: '', phone: '', email: '',
          priceSnow: '35.00', driveway: true, sidewalks: false, walkways: false, salt: false,
          paymentMethodPreferred: 'square', serviceFrequency: 'seasonal', notes: ''
        });
        alert(`New customer ${createdCustomer.first_name} ${createdCustomer.last_name} created and added to end of route.`);
      }
    } catch (err) {
      console.error('Failed to create customer:', err);
      alert(err.response?.data?.error || 'Failed to create customer');
    } finally {
      setAddingCustomer(false);
    }
  };

  // Filter customers by search
  const filteredCustomers = availableCustomers.filter(c => {
    if (!customerSearch) return true;
    const search = customerSearch.toLowerCase();
    return (
      c.first_name?.toLowerCase().includes(search) ||
      c.last_name?.toLowerCase().includes(search) ||
      c.address?.toLowerCase().includes(search) ||
      c.city?.toLowerCase().includes(search)
    );
  });

  // Helper to get current GPS position as a Promise
  const getCurrentGpsPosition = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation not supported'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lon: position.coords.longitude,
          });
        },
        (error) => {
          reject(error);
        },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
      );
    });
  };

  // Re-optimize remaining stops from current position
  const reoptimizeRemaining = async () => {
    if (getPendingStops().length < 2) {
      alert('Need at least 2 pending stops to optimize');
      return;
    }

    setReoptimizing(true);
    setGpsStatus('');

    try {
      let requestData = {};

      // If GPS optimization is enabled, try to get current location
      if (useGpsForOptimization) {
        setGpsStatus('acquiring');
        try {
          const position = await getCurrentGpsPosition();
          requestData = {
            currentLat: position.lat,
            currentLon: position.lon,
            useGps: true,
          };
          setGpsStatus('success');
        } catch (gpsError) {
          console.warn('GPS unavailable, using last stop:', gpsError.message);
          setGpsStatus('unavailable');
          requestData = { useGps: false };
        }
      } else {
        setGpsStatus('');
        requestData = { useGps: false };
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}/optimize-remaining`,
        requestData
      );

      if (response.data.optimized) {
        // Reload route to get new order
        const routeResponse = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/routes/${selectedRoute.id}`
        );
        setSelectedRoute(routeResponse.data);
        const locationMsg = response.data.usedGps
          ? 'your current GPS location'
          : 'the last completed stop';
        alert(`Route re-optimized from ${locationMsg}! ${response.data.stopsReordered} stops reordered.`);
      } else {
        alert(response.data.message || 'Route not optimized');
      }
    } catch (err) {
      console.error('Failed to re-optimize:', err);
      alert(err.response?.data?.error || 'Failed to re-optimize route');
    } finally {
      setReoptimizing(false);
      // Clear GPS status after a delay
      setTimeout(() => setGpsStatus(''), 3000);
    }
  };

  // Get current location info for display
  const getCurrentLocation = () => {
    const completedStops = getCompletedStops();
    if (completedStops.length > 0) {
      // Sort by completed_at descending to get the most recent
      const sorted = [...completedStops].sort((a, b) =>
        new Date(b.completed_at) - new Date(a.completed_at)
      );
      const lastStop = sorted[0];
      return {
        type: 'stop',
        name: `${lastStop.first_name} ${lastStop.last_name}`,
        address: lastStop.address,
      };
    }
    if (selectedRoute?.starting_address) {
      return {
        type: 'start',
        name: 'Starting Address',
        address: selectedRoute.starting_address,
      };
    }
    return null;
  };

  const progress = getProgress();

  return (
    <div className="service-mode-page">
      {/* Trial Expired Modal */}
      {showTrialModal && (
        <TrialExpired
          variant="modal"
          onClose={() => setShowTrialModal(false)}
        />
      )}
      {/* Header */}
      <header className="app-header storm-header">
        <div className="app-header-content">
          <span className="app-logo" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
            {getAppName()}
          </span>
          <nav className="header-nav desktop-nav">
            <button onClick={() => navigate('/dashboard')} className="nav-link">Dashboard</button>
            {!isDriver() && <button onClick={() => navigate('/customers')} className="nav-link">Customers</button>}
            <button onClick={() => navigate('/routes')} className="nav-link">Routes</button>
            {isAdmin() && <button onClick={() => navigate('/history')} className="nav-link">History</button>}
            {isAdmin() && <button onClick={() => navigate('/expenses')} className="nav-link">Expenses</button>}
            {isAdmin() && <button onClick={() => navigate('/reports')} className="nav-link">Reports</button>}
            <span className="nav-link active storm-active">Service Mode</span>
            <button onClick={() => navigate('/settings')} className="nav-link">Settings</button>
            <button onClick={() => navigate('/docs')} className="nav-link help-link" title="Help">?</button>
            {isSuperAdmin() && (
              <button onClick={() => navigate('/admin')} className="nav-link admin-link">Admin</button>
            )}
            <button onClick={logout} className="nav-link logout-link">Logout</button>
          </nav>
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
        {mobileMenuOpen && (
          <nav className="mobile-nav">
            <button onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} className="mobile-nav-link">Dashboard</button>
            {!isDriver() && <button onClick={() => { navigate('/customers'); setMobileMenuOpen(false); }} className="mobile-nav-link">Customers</button>}
            <button onClick={() => { navigate('/routes'); setMobileMenuOpen(false); }} className="mobile-nav-link">Routes</button>
            {isAdmin() && <button onClick={() => { navigate('/history'); setMobileMenuOpen(false); }} className="mobile-nav-link">History</button>}
            {isAdmin() && <button onClick={() => { navigate('/expenses'); setMobileMenuOpen(false); }} className="mobile-nav-link">Expenses</button>}
            {isAdmin() && <button onClick={() => { navigate('/reports'); setMobileMenuOpen(false); }} className="mobile-nav-link">Reports</button>}
            <span className="mobile-nav-link active storm-active">Service Mode</span>
            <button onClick={() => { navigate('/settings'); setMobileMenuOpen(false); }} className="mobile-nav-link">Settings</button>
            <button onClick={() => { navigate('/docs'); setMobileMenuOpen(false); }} className="mobile-nav-link">Help</button>
            {isSuperAdmin() && (
              <button onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }} className="mobile-nav-link admin-link">Admin</button>
            )}
            <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="mobile-nav-link logout-link">Logout</button>
          </nav>
        )}
      </header>

      <PullToRefreshIndicator />

      <div className="page-container storm-container">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        ) : !selectedRoute ? (
          /* Route Selection */
          <div className="route-selection">
            <h1 className="storm-title">Service Mode</h1>
            <p className="storm-subtitle">Select a route to begin servicing</p>

            {/* Route Filter Tabs and Sort Toggle */}
            <div className="route-controls">
              <div className="route-filter-tabs">
                <button
                  className={`filter-tab ${routeFilter === 'active' ? 'active' : ''}`}
                  onClick={() => setRouteFilter('active')}
                >
                  Active ({routes.filter(r => r.status !== 'completed' && !r.is_archived).length})
                </button>
                <button
                  className={`filter-tab ${routeFilter === 'completed' ? 'active' : ''}`}
                  onClick={() => setRouteFilter('completed')}
                >
                  Completed ({routes.filter(r => r.status === 'completed' && !r.is_archived).length})
                </button>
                <button
                  className={`filter-tab ${routeFilter === 'archived' ? 'active' : ''}`}
                  onClick={() => setRouteFilter('archived')}
                >
                  Archived ({routes.filter(r => r.is_archived).length})
                </button>
              </div>
              <button className="sort-toggle" onClick={toggleRouteSortOrder}>
                {routeSortOrder === 'newest' ? '↓ Newest' : '↑ Oldest'}
              </button>
            </div>

            {routes.length === 0 ? (
              <div className="empty-state">
                <p>No routes available</p>
                <button className="btn btn-primary" onClick={() => navigate('/routes')}>
                  Create a Route
                </button>
              </div>
            ) : (
              <div className="route-select-grid">
                {routes
                  .filter(route => {
                    if (routeFilter === 'active') return route.status !== 'completed' && !route.is_archived;
                    if (routeFilter === 'completed') return route.status === 'completed' && !route.is_archived;
                    if (routeFilter === 'archived') return route.is_archived;
                    return true;
                  })
                  .sort((a, b) => {
                    const dateA = new Date(a.created_at || 0);
                    const dateB = new Date(b.created_at || 0);
                    return routeSortOrder === 'newest' ? dateB - dateA : dateA - dateB;
                  })
                  .map((route) => (
                  <button
                    key={route.id}
                    className={`route-select-card ${route.status === 'completed' ? 'completed' : ''} ${route.is_archived ? 'archived' : ''}`}
                    onClick={() => handleRouteClick(route)}
                  >
                    <div className="route-select-name">{route.name}</div>
                    <div className="route-select-meta">
                      {route.status === 'in_progress' && (
                        <span className="status-badge in-progress">In Progress</span>
                      )}
                      {route.status === 'completed' && (
                        <span className="status-badge completed">Completed</span>
                      )}
                      {route.status !== 'in_progress' && route.status !== 'completed' && (
                        <span className="status-badge not-started">Not Started</span>
                      )}
                      {route.is_archived && (
                        <span className="status-badge archived">Archived</span>
                      )}
                      <span>{route.stop_count || 0} stops</span>
                      {!shouldHidePrices() && (
                        <span className="route-total">${parseFloat(route.total_price || 0).toFixed(2)}</span>
                      )}
                      {isDriver() && route.driver_pay_type && route.driver_pay_rate && (
                        <span className="driver-pay-badge">
                          Your pay: ${parseFloat(route.driver_pay_rate).toFixed(2)}{route.driver_pay_type === 'hourly' ? '/hr' : ''}
                        </span>
                      )}
                      {isAdmin() && (
                        route.assigned_to_name ? (
                          <span className="assigned-badge">{route.assigned_to_name}</span>
                        ) : (
                          <span className="assigned-badge unassigned">Unassigned</span>
                        )
                      )}
                      {!shouldHidePrices() && route.status === 'completed' && parseFloat(route.total_price || 0) > 0 && (
                        <span className="payment-breakdown">
                          {parseFloat(route.total_unpaid || 0) === 0 ? (
                            <span className="payment-fully-paid">✓ Paid</span>
                          ) : parseFloat(route.total_paid || 0) === 0 ? (
                            <span className="payment-unpaid">${parseFloat(route.total_unpaid || 0).toFixed(2)} unpaid</span>
                          ) : (
                            <>
                              <span className="payment-paid">${parseFloat(route.total_paid || 0).toFixed(2)}</span>
                              <span className="payment-separator">|</span>
                              <span className="payment-unpaid">${parseFloat(route.total_unpaid || 0).toFixed(2)}</span>
                            </>
                          )}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
                {routes.filter(route => {
                  if (routeFilter === 'active') return route.status !== 'completed' && !route.is_archived;
                  if (routeFilter === 'completed') return route.status === 'completed' && !route.is_archived;
                  if (routeFilter === 'archived') return route.is_archived;
                  return true;
                }).length === 0 && (
                  <div className="empty-filter-state">
                    <p>No {routeFilter} routes</p>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Active Route View */
          <div className="active-route">
            {/* Route Header */}
            <div className="route-header">
              <button className="btn btn-outline btn-back" onClick={() => setSelectedRoute(null)}>
                Back
              </button>
              <div className="route-header-info">
                <h1 className="route-title">{selectedRoute.name}</h1>
                <div className="progress-info">
                  {progress.completed} / {progress.total} stops
                  {selectedRoute.status === 'completed' && (
                    <span className="route-status-badge completed">Completed</span>
                  )}
                  {selectedRoute.is_archived && (
                    <span className="route-status-badge archived">Archived</span>
                  )}
                  {isAdmin() && (
                    selectedRoute.assigned_to_name ? (
                      <span className="assigned-badge">{selectedRoute.assigned_to_name}</span>
                    ) : (
                      <span className="assigned-badge unassigned">Unassigned</span>
                    )
                  )}
                </div>
              </div>
              <div className="route-header-actions">
                {selectedRoute.status !== 'completed' && !selectedRoute.is_archived && (
                  <button className="btn btn-primary btn-sm" onClick={openAddCustomerModal}>
                    + Add Stop
                  </button>
                )}
                {selectedRoute.status === 'completed' && (
                  <button className="btn btn-outline btn-sm" onClick={() => setShowPrintView(true)}>
                    Print Summary
                  </button>
                )}
                <button className="btn btn-outline btn-sm" onClick={toggleArchiveRoute}>
                  {selectedRoute.is_archived ? 'Unarchive' : 'Archive'}
                </button>
                {selectedRoute.status !== 'completed' && !selectedRoute.is_archived && (
                  <button className="btn btn-outline btn-sm" onClick={resetRoute}>
                    Reset
                  </button>
                )}
              </div>
            </div>

            {/* Progress Bar */}
            <div className="progress-bar-container">
              <div
                className="progress-bar-fill"
                style={{ width: `${progress.percentage}%` }}
              ></div>
            </div>

            {/* Current Location & Re-optimize */}
            {getCurrentLocation() && (
              <div className="current-location-bar">
                <div className="current-location-info">
                  <span className="current-location-label">Current Location:</span>
                  <span className="current-location-name">{getCurrentLocation().name}</span>
                  <span className="current-location-address">({getCurrentLocation().address})</span>
                </div>
                {getPendingStops().length >= 2 && (
                  <div className="reoptimize-container">
                    {gpsStatus && (
                      <span className={`gps-status gps-status-${gpsStatus}`}>
                        {gpsStatus === 'acquiring' && '📍 Acquiring GPS...'}
                        {gpsStatus === 'success' && '📍 Using GPS location'}
                        {gpsStatus === 'unavailable' && '⚠️ GPS unavailable, using last stop'}
                      </span>
                    )}
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={reoptimizeRemaining}
                      disabled={reoptimizing}
                    >
                      {reoptimizing ? 'Optimizing...' : 'Re-optimize Remaining'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Stops List */}
            {getPendingStops().length === 0 && getCompletedStops().length > 0 ? (
              /* Completed Route View - show all stops with details */
              <div className="completed-route-view">
                <div className="route-complete-banner">
                  <span className="complete-check">✓</span>
                  <span>Route Complete - {getCompletedStops().filter(s => s.status === 'completed').length} completed
                  {getCompletedStops().filter(s => s.status === 'skipped').length > 0 &&
                    `, ${getCompletedStops().filter(s => s.status === 'skipped').length} skipped`}
                  </span>
                  {!shouldHidePrices() && (
                    <span className="route-total-banner">Total: ${calculateTotal().toFixed(2)}</span>
                  )}
                </div>

                {!shouldHidePrices() && (
                  <div className="payment-summary-bar">
                    <div className="payment-summary-item paid">
                      <span className="payment-summary-label">Paid</span>
                      <span className="payment-summary-amount">${calculatePaidTotal().toFixed(2)}</span>
                    </div>
                    <div className="payment-summary-divider"></div>
                    <div className="payment-summary-item unpaid">
                      <span className="payment-summary-label">Unpaid</span>
                      <span className="payment-summary-amount">${calculateUnpaidTotal().toFixed(2)}</span>
                    </div>
                    <div className="payment-summary-divider"></div>
                    <div className="payment-summary-item total">
                      <span className="payment-summary-label">Total</span>
                      <span className="payment-summary-amount">${calculateTotal().toFixed(2)}</span>
                    </div>
                  </div>
                )}

                <div className="complete-actions-bar">
                  {!isDriver() && (
                    <button className="btn btn-secondary" onClick={handlePrint}>
                      Print Summary
                    </button>
                  )}
                  {!isDriver() && (isPro() || isSuperAdmin()) && (
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        setBillingResults(null);
                        setShowBillingModal(true);
                      }}
                    >
                      Bill All
                    </button>
                  )}
                  <button className="btn btn-outline" onClick={() => setSelectedRoute(null)}>
                    Return to Routes
                  </button>
                </div>

                {/* All Completed/Skipped Stops */}
                <div className="stops-section">
                  <h2 className="stops-section-title">
                    All Stops ({getCompletedStops().length})
                  </h2>
                  <div className="completed-stops-detail-list">
                    {getCompletedStops().map((stop, index) => (
                      <div
                        key={stop.id}
                        className={`completed-stop-card ${stop.status === 'skipped' ? 'skipped' : ''}`}
                        onClick={() => stop.status !== 'skipped' && editPaymentStatus(stop)}
                      >
                        <div className="stop-order-badge">{index + 1}</div>
                        <div className="stop-details">
                          <div className="stop-customer-name">
                            {stop.first_name} {stop.last_name}
                            {stop.status === 'skipped' && <span className="skipped-label">Skipped</span>}
                            {/* Payment method preference - visible to all including drivers */}
                            {stop.payment_method_preferred && (
                              <span className={`payment-method-badge ${stop.payment_method_preferred}`}>
                                {stop.payment_method_preferred === 'cash' ? 'Cash' :
                                 stop.payment_method_preferred === 'venmo' ? 'Venmo' :
                                 stop.payment_method_preferred === 'square' ? 'Square' :
                                 stop.payment_method_preferred === 'zelle' ? 'Zelle' :
                                 stop.payment_method_preferred === 'check' ? 'Check' :
                                 stop.payment_method_preferred === 'cashapp' ? 'Cash App' : stop.payment_method_preferred}
                              </span>
                            )}
                          </div>
                          <div className="stop-address">{stop.address}</div>
                          <div className="stop-city">{stop.city}, {stop.state} {stop.zip}</div>
                          {stop.phone && (
                            <button
                              className="stop-phone"
                              onClick={e => handlePhoneClick(e, stop.phone)}
                            >
                              {formatPhoneDisplay(stop.phone)}
                            </button>
                          )}
                          {!shouldHidePrices() && stop.status !== 'skipped' && (() => {
                            const extraChargeAmt = parseFloat(stop.extra_charge_amount || 0);
                            const basePrice = parseFloat(stop.price_snow || 50) + (shouldIncludeSalt(stop) ? parseFloat(stop.salt_price || 0) : 0);
                            const basePriceWithExtra = basePrice + extraChargeAmt;
                            const hasStopDiscount = stop.discount_amount && stop.discount_type;
                            const hasCustomerDiscount = stop.customer_discount_amount && stop.customer_discount_type;
                            const hasReferralDiscount = referralDiscounts[stop.customer_id] && referralDiscounts[stop.customer_id].discount > 0;
                            const hasAnyDiscount = hasStopDiscount || hasCustomerDiscount || hasReferralDiscount;
                            const effectivePrice = getEffectivePrice(stop);
                            return (
                            <div className="stop-price">
                              {hasAnyDiscount ? (
                                <>
                                  <span className="original-price">${basePriceWithExtra.toFixed(2)}</span>
                                  <span className="discounted-price">${effectivePrice.toFixed(2)}</span>
                                </>
                              ) : (
                                <span className="regular-price">
                                  ${basePriceWithExtra.toFixed(2)}
                                </span>
                              )}
                              {stop.salt && <span className="salt-badge">Fertilizer</span>}
                              {extraChargeAmt > 0 && (
                                <span className="extra-charge-badge" title={stop.extra_charge_reason || 'Extra charge'}>
                                  +${extraChargeAmt.toFixed(2)}
                                </span>
                              )}
                              {/* Payment method badge shown at top for all users */}
                            </div>
                          );})()}
                          {stop.notes && (
                            <div className="stop-notes">{stop.notes}</div>
                          )}
                        </div>
                        {!shouldHidePrices() && (
                          <div className="stop-payment-status">
                            {stop.status === 'skipped' ? (
                              <span className="payment-badge skipped">Skipped</span>
                            ) : stop.payment_method?.toLowerCase().includes('square') ? (
                              /* Square customers get special badge based on invoice/payment state */
                              stop.payment_status === 'paid' ? (
                                <span className="square-badge paid">Square - Paid</span>
                              ) : stop.invoice_sent_at ? (
                                <span className="square-badge invoice-sent">Invoice Sent</span>
                              ) : (
                                <span className="square-badge pending">Square</span>
                              )
                            ) : (
                              /* Non-Square payment methods */
                              <span className={`payment-badge ${
                                stop.payment_status === 'paid' ? 'paid' : 'not-paid'
                              }`}>
                                {getPaymentLabel(stop)}
                              </span>
                            )}
                            {stop.status !== 'skipped' && (
                              <span className="tap-to-edit">Tap to edit</span>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Pending Stops */}
                <div className="stops-section">
                  <h2 className="stops-section-title">
                    Up Next ({getPendingStops().length})
                  </h2>
                  <div className="storm-stops-list">
                    {getPendingStops().map((stop, index) => (
                      <div
                        key={stop.id}
                        className={`storm-stop-card ${index === 0 ? 'next-up' : ''}`}
                      >
                        <div className="stop-order-badge">{index + 1}</div>
                        <div className="stop-details">
                          <div className="stop-customer-name">
                            {stop.first_name} {stop.last_name}
                            <span className={`service-frequency-badge ${stop.service_frequency === 'as_needed' ? 'as-needed' : 'seasonal'}`}>
                              {stop.service_frequency === 'as_needed' ? 'As Needed' : 'Seasonal'}
                            </span>
                            {/* Payment method preference - visible to all including drivers */}
                            {stop.payment_method_preferred && (
                              <span className={`payment-method-badge ${stop.payment_method_preferred}`}>
                                {stop.payment_method_preferred === 'cash' ? 'Cash' :
                                 stop.payment_method_preferred === 'venmo' ? 'Venmo' :
                                 stop.payment_method_preferred === 'square' ? 'Square' :
                                 stop.payment_method_preferred === 'zelle' ? 'Zelle' :
                                 stop.payment_method_preferred === 'check' ? 'Check' :
                                 stop.payment_method_preferred === 'cashapp' ? 'Cash App' : stop.payment_method_preferred}
                              </span>
                            )}
                          </div>
                          <div className="stop-address">{stop.address}</div>
                          <div className="stop-city">{stop.city}, {stop.state} {stop.zip}</div>
                          {stop.phone && (
                            <button
                              className="stop-phone"
                              onClick={e => handlePhoneClick(e, stop.phone)}
                            >
                              {formatPhoneDisplay(stop.phone)}
                            </button>
                          )}
                          {!shouldHidePrices() && (
                            <div className="stop-price">
                              {/* Check for customer-level ongoing discount first */}
                              {stop.customer_discount_amount && stop.customer_discount_type ? (
                                <>
                                  <span className="original-price">
                                    ${(parseFloat(stop.price_snow || 50) + (shouldIncludeSalt(stop) ? parseFloat(stop.salt_price || 0) : 0)).toFixed(2)}
                                  </span>
                                  <span className="discounted-price">
                                    ${getEffectivePrice(stop).toFixed(2)}
                                  </span>
                                  {shouldIncludeSalt(stop) && <span className="salt-badge">Fertilizer</span>}
                                  {/* Payment method badge shown at top for all users */}
                                  <span className="discount-badge">
                                    {stop.customer_discount_type === 'percent'
                                      ? `${stop.customer_discount_amount}% off`
                                      : `$${parseFloat(stop.customer_discount_amount).toFixed(2)} off`}
                                  </span>
                                </>
                              ) : referralDiscounts[stop.customer_id] && referralDiscounts[stop.customer_id].expectedDiscount > 0 ? (
                                <>
                                  <span className="original-price">${referralDiscounts[stop.customer_id].basePrice.toFixed(2)}</span>
                                  <span className="discounted-price">${referralDiscounts[stop.customer_id].expectedPrice.toFixed(2)}</span>
                                  {referralDiscounts[stop.customer_id].saltIncluded && (
                                    <span className="salt-badge">Fertilizer</span>
                                  )}
                                  {/* Payment method badge shown at top for all users */}
                                  <span className="referral-discount-badge">
                                    {referralDiscounts[stop.customer_id].totalReferrals} referral{referralDiscounts[stop.customer_id].totalReferrals !== 1 ? 's' : ''} (-${referralDiscounts[stop.customer_id].expectedDiscount})
                                  </span>
                                </>
                              ) : (
                                <>
                                  <span className="regular-price">
                                    ${(parseFloat(stop.price_snow || 50) + (shouldIncludeSalt(stop) ? parseFloat(stop.salt_price || 0) : 0)).toFixed(2)}
                                  </span>
                                  {shouldIncludeSalt(stop) && <span className="salt-badge">Fertilizer</span>}
                                  {/* Payment method badge shown at top for all users */}
                                </>
                              )}
                            </div>
                          )}
                          {!shouldHidePrices() && stop.customer_discount_reason && (
                            <div className="stop-discount-reason">{stop.customer_discount_reason}</div>
                          )}
                          {stop.notes && (
                            <div className="stop-notes">{stop.notes}</div>
                          )}
                        </div>
                        <div className="stop-actions">
                          <button
                            className="btn btn-lg btn-success stop-complete-btn"
                            onClick={() => markStopComplete(stop)}
                            disabled={completing === stop.id}
                          >
                            {completing === stop.id ? '...' : 'Done'}
                          </button>
                          <div className="stop-secondary-actions">
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => openNavigation(stop)}
                            >
                              Navigate
                            </button>
                            <button
                              className="btn btn-sm btn-outline"
                              onClick={() => markStopSkipped(stop)}
                              disabled={completing === stop.id}
                            >
                              Skip
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Completed Stops */}
                {getCompletedStops().length > 0 && (
                  <div className="stops-section completed-section">
                    <h2 className="stops-section-title">
                      Completed ({getCompletedStops().length})
                    </h2>
                    <div className="completed-stops-list">
                      {getCompletedStops().map((stop) => {
                        const extraChargeAmt = parseFloat(stop.extra_charge_amount || 0);
                        const basePrice = parseFloat(stop.price_snow || 50) + (shouldIncludeSalt(stop) ? parseFloat(stop.salt_price || 0) : 0);
                        const totalPrice = basePrice + extraChargeAmt;
                        const hasDiscount = (stop.discount_amount && stop.discount_type) || (stop.customer_discount_amount && stop.customer_discount_type);
                        const effectivePrice = getEffectivePrice(stop);
                        // Get discount info from stop-level first, then customer-level
                        const discountInfo = stop.discount_amount && stop.discount_type
                          ? { amount: stop.discount_amount, type: stop.discount_type, reason: stop.discount_reason }
                          : stop.customer_discount_amount && stop.customer_discount_type
                          ? { amount: stop.customer_discount_amount, type: stop.customer_discount_type, reason: stop.customer_discount_reason }
                          : null;
                        return (
                          <div
                            key={stop.id}
                            className={`completed-stop-item ${stop.status !== 'skipped' ? 'editable' : ''} ${hasDiscount ? 'has-discount' : ''}`}
                            onClick={() => stop.status !== 'skipped' && editPaymentStatus(stop)}
                          >
                            <span className={`completed-icon ${stop.status === 'skipped' ? 'skipped' : ''}`}>
                              {stop.status === 'skipped' ? '—' : '✓'}
                            </span>
                            <div className="completed-info">
                              <span className="completed-name">
                                {stop.first_name} {stop.last_name}
                                {!shouldHidePrices() && extraChargeAmt > 0 && (
                                  <span className="extra-charge-indicator" title={stop.extra_charge_reason || 'Extra charge'}>
                                    +${extraChargeAmt.toFixed(2)}
                                  </span>
                                )}
                              </span>
                              {!shouldHidePrices() && hasDiscount && discountInfo && (
                                <span className="completed-discount-info">
                                  {discountInfo.type === 'percent'
                                    ? `${discountInfo.amount}% off`
                                    : `$${parseFloat(discountInfo.amount).toFixed(2)} off`}
                                  {discountInfo.reason && ` - ${discountInfo.reason}`}
                                </span>
                              )}
                            </div>
                            {!shouldHidePrices() && (
                              <span className="completed-price">
                                {stop.is_comp ? (
                                  <span className="comp-badge">COMP</span>
                                ) : hasDiscount ? (
                                  <>
                                    <span className="price-strike">${totalPrice.toFixed(2)}</span>
                                    <span className="price-discounted">${effectivePrice.toFixed(2)}</span>
                                  </>
                                ) : (
                                  `$${totalPrice.toFixed(2)}`
                                )}
                              </span>
                            )}
                            {!shouldHidePrices() && (stop.status === 'skipped' ? (
                              <span className="payment-badge skipped">Skipped</span>
                            ) : stop.payment_method?.toLowerCase().includes('square') ? (
                              /* Square customers get special badge based on invoice/payment state */
                              stop.payment_status === 'paid' ? (
                                <span className="square-badge paid">Square - Paid</span>
                              ) : stop.invoice_sent_at ? (
                                <span className="square-badge invoice-sent">Invoice Sent</span>
                              ) : (
                                <span className="square-badge pending">Square</span>
                              )
                            ) : (
                              /* Non-Square payment methods */
                              <span className={`payment-badge ${
                                stop.payment_status === 'paid' ? 'paid' : 'not-paid'
                              }`}>
                                {getPaymentLabel(stop)}
                              </span>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Print View */}
      {showPrintView && (
        <div className="print-view" ref={printRef}>
          <div className="print-header">
            <h1>{getAppName()} - Route Summary</h1>
            <div className="print-meta">
              <p><strong>Date:</strong> {new Date().toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}</p>
              <p><strong>Route:</strong> {selectedRoute?.name}</p>
            </div>
          </div>

          <table className="print-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Customer</th>
                <th>Address</th>
                <th>Services</th>
                <th className="text-right">Price</th>
                <th>Payment</th>
              </tr>
            </thead>
            <tbody>
              {selectedRoute?.stops?.filter(s => s.status === 'completed').map((stop, index) => {
                const basePrice = parseFloat(stop.price_snow || 50) + (shouldIncludeSalt(stop) ? parseFloat(stop.salt_price || 0) : 0);
                const hasDiscount = (stop.discount_amount && stop.discount_type) || (stop.customer_discount_amount && stop.customer_discount_type);
                const finalPrice = getEffectivePrice(stop);
                const discountInfo = stop.discount_amount && stop.discount_type
                  ? { amount: stop.discount_amount, type: stop.discount_type, reason: stop.discount_reason }
                  : stop.customer_discount_amount && stop.customer_discount_type
                  ? { amount: stop.customer_discount_amount, type: stop.customer_discount_type, reason: stop.customer_discount_reason }
                  : null;
                const paymentLabel = getPaymentLabel(stop);
                return (
                  <tr key={stop.id}>
                    <td>{index + 1}</td>
                    <td>
                      {stop.first_name} {stop.last_name}
                      {discountInfo && discountInfo.reason && (
                        <div style={{fontSize: '0.75rem', color: '#059669', fontStyle: 'italic'}}>
                          {discountInfo.reason}
                        </div>
                      )}
                    </td>
                    <td>{stop.address}, {stop.city}</td>
                    <td>{getServicesText(stop)}</td>
                    <td className="text-right">
                      {hasDiscount ? (
                        <span>
                          <span style={{textDecoration: 'line-through', color: '#999', marginRight: '8px'}}>
                            ${basePrice.toFixed(2)}
                          </span>
                          <span style={{color: '#059669', fontWeight: '600'}}>
                            ${finalPrice.toFixed(2)}
                          </span>
                          <div style={{fontSize: '0.7rem', color: '#059669'}}>
                            {discountInfo?.type === 'percent' ? `${discountInfo.amount}% off` : `$${parseFloat(discountInfo?.amount || 0).toFixed(2)} off`}
                          </div>
                        </span>
                      ) : (
                        `$${finalPrice.toFixed(2)}`
                      )}
                    </td>
                    <td className={`payment-status ${stop.payment_status || 'not-paid'}`}>{paymentLabel}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="5" className="text-right"><strong>Route Revenue:</strong></td>
                <td className="text-right"><strong>${calculateTotal().toFixed(2)}</strong></td>
              </tr>
            </tfoot>
          </table>

          {/* Route Expenses Section */}
          {routeExpenses.length > 0 && (
            <div className="print-expenses-section">
              <h3>Route Expenses</h3>
              <table className="print-table expenses-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Category</th>
                    <th>Description</th>
                    <th className="text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {routeExpenses.map((expense) => (
                    <tr key={expense.id}>
                      <td>{expense.date || 'N/A'}</td>
                      <td style={{ textTransform: 'capitalize' }}>{expense.category?.replace('_', ' ')}</td>
                      <td>{expense.description || '-'}</td>
                      <td className="text-right" style={{ color: '#dc2626' }}>-${parseFloat(expense.amount).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3" className="text-right"><strong>Total Expenses:</strong></td>
                    <td className="text-right" style={{ color: '#dc2626' }}>
                      <strong>-${routeExpenses.reduce((sum, e) => sum + parseFloat(e.amount), 0).toFixed(2)}</strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Net Profit Summary */}
          <div className="print-profit-summary">
            <div className="profit-row">
              <span>Route Revenue:</span>
              <span className="profit-amount">${calculateTotal().toFixed(2)}</span>
            </div>
            {routeExpenses.length > 0 && (
              <>
                <div className="profit-row">
                  <span>Total Expenses:</span>
                  <span className="profit-amount" style={{ color: '#dc2626' }}>
                    -${routeExpenses.reduce((sum, e) => sum + parseFloat(e.amount), 0).toFixed(2)}
                  </span>
                </div>
                <div className="profit-row net">
                  <span><strong>Net Profit:</strong></span>
                  <span className="profit-amount">
                    <strong>${(calculateTotal() - routeExpenses.reduce((sum, e) => sum + parseFloat(e.amount), 0)).toFixed(2)}</strong>
                  </span>
                </div>
              </>
            )}
          </div>

          <div className="print-footer">
            <p>Stops completed: {selectedRoute?.stops?.filter(s => s.status === 'completed').length}</p>
            <p>Paid: {selectedRoute?.stops?.filter(s => s.status === 'completed' && s.payment_status === 'paid').length} |
               Unpaid: {selectedRoute?.stops?.filter(s => s.status === 'completed' && s.payment_status !== 'paid').length}</p>
          </div>

          <button className="print-close-btn no-print" onClick={() => setShowPrintView(false)}>
            Close Preview
          </button>
        </div>
      )}

      {/* Start Route Modal */}
      {showStartRouteModal && pendingRouteToStart && (
        <div className="modal-overlay" onClick={() => { setShowStartRouteModal(false); setPendingRouteToStart(null); }}>
          <div className="start-route-modal" onClick={(e) => e.stopPropagation()}>
            <div className="start-route-modal-header">
              <h2>Start Route</h2>
              <p className="start-route-name">{pendingRouteToStart.name}</p>
            </div>

            <div className="start-route-modal-body">
              <p className="start-route-question">Where are you starting from?</p>

              {/* Saved Location Option */}
              <label className={`start-location-option ${startLocationOption === 'saved' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="startLocation"
                  value="saved"
                  checked={startLocationOption === 'saved'}
                  onChange={() => setStartLocationOption('saved')}
                />
                <div className="option-content">
                  <span className="option-label">Saved location</span>
                  <span className="option-address">
                    {pendingRouteToStart.starting_address
                      ? `${pendingRouteToStart.starting_address}, ${pendingRouteToStart.starting_city || ''} ${pendingRouteToStart.starting_state || ''}`
                      : 'No saved starting address'}
                  </span>
                </div>
              </label>

              {/* GPS Location Option */}
              <label className={`start-location-option ${startLocationOption === 'gps' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="startLocation"
                  value="gps"
                  checked={startLocationOption === 'gps'}
                  onChange={() => {
                    setStartLocationOption('gps');
                    if (!gpsStartLocation && gpsStartStatus !== 'detecting') {
                      detectGpsForStart();
                    }
                  }}
                />
                <div className="option-content">
                  <span className="option-label">My current GPS location</span>
                  <span className="option-address gps-address">
                    {gpsStartStatus === 'detecting' && '📍 Detecting location...'}
                    {gpsStartStatus === 'success' && gpsStartLocation && `📍 ${gpsStartLocation.address}`}
                    {gpsStartStatus === 'error' && '⚠️ GPS unavailable'}
                    {gpsStartStatus === '' && startLocationOption === 'gps' && '📍 Click to detect...'}
                  </span>
                </div>
              </label>
            </div>

            <div className="start-route-modal-footer">
              <button
                className="btn btn-outline"
                onClick={() => { setShowStartRouteModal(false); setPendingRouteToStart(null); }}
                disabled={startingRoute}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={confirmStartRoute}
                disabled={startingRoute || (startLocationOption === 'gps' && gpsStartStatus === 'detecting')}
              >
                {startingRoute ? 'Starting...' : 'Start Route'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && pendingPaymentStop && (
        <div className="modal-overlay" onClick={() => { setShowPaymentModal(false); setPendingPaymentStop(null); setIsEditingPayment(false); setShowDiscountForm(false); setShowExtraChargeForm(false); setExtraCharge({ amount: '', reason: '' }); setIsComp(false); setCompReason(''); setStopPhotos([]); setSelectedPhoto(null); setSelectedAddons({}); }}>
          <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
            <div className="payment-modal-header">
              <h2>{isEditingPayment ? 'Update Payment' : 'Mark as Paid?'}</h2>
              <p className="payment-customer-name">
                {pendingPaymentStop.first_name} {pendingPaymentStop.last_name}
              </p>
              <p className="payment-amount">
                {(() => {
                  const basePrice = parseFloat(pendingPaymentStop.price_snow) || 35;
                  const extraChargeAmount = parseFloat(extraCharge.amount) || 0;
                  const addonsAmount = availableAddons
                    .filter(a => selectedAddons[a.id])
                    .map(a => {
                      const customerTierId = pendingPaymentStop?.lawn_tier_id || pendingPaymentStop?.pricing_tier_id;
                      const tierPrice = customerTierId && a.tier_prices
                        ? a.tier_prices.find(tp => tp.pricing_tier_id === customerTierId)?.price
                        : undefined;
                      return tierPrice !== undefined ? tierPrice : parseFloat(a.price);
                    })
                    .reduce((sum, p) => sum + p, 0);
                  const totalBeforeDiscount = basePrice + extraChargeAmount + addonsAmount;

                  if (stopDiscount.type && stopDiscount.amount) {
                    const finalPrice = calculateDiscountedPrice(totalBeforeDiscount, parseFloat(stopDiscount.amount), stopDiscount.type);
                    return (
                      <>
                        <span className="original-price-strike">${totalBeforeDiscount.toFixed(2)}</span>
                        <span className="discounted-price-display">
                          ${finalPrice.toFixed(2)}
                        </span>
                      </>
                    );
                  }
                  return `$${totalBeforeDiscount.toFixed(2)}`;
                })()}
                {serviceChecklist.fertilizer && <span className="fertilizer-indicator-modal"> +Fert</span>}
                {Object.values(selectedAddons).some(v => v) && <span className="addons-indicator-modal"> +Add-ons</span>}
                {extraCharge.amount && parseFloat(extraCharge.amount) > 0 && <span className="extra-charge-indicator-modal"> +Extra</span>}
              </p>
            </div>

            {/* Service Checklist Section */}
            <div className="service-checklist-section">
              <label className="service-checklist-title">Services Performed:</label>
              <div className="service-checklist-grid">
                <label className="service-checkbox-label">
                  <input
                    type="checkbox"
                    checked={serviceChecklist.mow}
                    onChange={(e) => setServiceChecklist(prev => ({ ...prev, mow: e.target.checked }))}
                  />
                  <span className="service-checkbox-text">Mow</span>
                </label>
                <label className="service-checkbox-label">
                  <input
                    type="checkbox"
                    checked={serviceChecklist.edge}
                    onChange={(e) => setServiceChecklist(prev => ({ ...prev, edge: e.target.checked }))}
                  />
                  <span className="service-checkbox-text">Edge</span>
                </label>
                <label className="service-checkbox-label">
                  <input
                    type="checkbox"
                    checked={serviceChecklist.trim}
                    onChange={(e) => setServiceChecklist(prev => ({ ...prev, trim: e.target.checked }))}
                  />
                  <span className="service-checkbox-text">Trim</span>
                </label>
                <label className="service-checkbox-label">
                  <input
                    type="checkbox"
                    checked={serviceChecklist.blow}
                    onChange={(e) => setServiceChecklist(prev => ({ ...prev, blow: e.target.checked }))}
                  />
                  <span className="service-checkbox-text">Blow</span>
                </label>
                <label className="service-checkbox-label">
                  <input
                    type="checkbox"
                    checked={serviceChecklist.fertilizer}
                    onChange={(e) => setServiceChecklist(prev => ({ ...prev, fertilizer: e.target.checked }))}
                  />
                  <span className="service-checkbox-text">Fertilizer</span>
                </label>
              </div>
            </div>

            {/* Service Add-ons Section */}
            {availableAddons.length > 0 && (
              <div className="service-addons-section">
                <label className="service-addons-title">Add-on Services:</label>
                <div className="service-addons-list">
                  {availableAddons.map(addon => {
                    const customerTierId = pendingPaymentStop?.lawn_tier_id || pendingPaymentStop?.pricing_tier_id;
                    const tierPrice = customerTierId && addon.tier_prices
                      ? addon.tier_prices.find(tp => tp.pricing_tier_id === customerTierId)?.price
                      : undefined;
                    const resolvedPrice = tierPrice !== undefined ? tierPrice : parseFloat(addon.price);
                    return (
                      <label key={addon.id} className="service-addon-item">
                        <input
                          type="checkbox"
                          checked={selectedAddons[addon.id] || false}
                          onChange={(e) => setSelectedAddons(prev => ({
                            ...prev,
                            [addon.id]: e.target.checked
                          }))}
                        />
                        <span className="addon-name">{addon.name}</span>
                        <span className="addon-price">+${resolvedPrice.toFixed(2)}</span>
                      </label>
                    );
                  })}
                </div>
                {Object.values(selectedAddons).some(v => v) && (
                  <div className="addons-total">
                    Add-ons: +${availableAddons
                      .filter(a => selectedAddons[a.id])
                      .map(a => {
                        const customerTierId = pendingPaymentStop?.lawn_tier_id || pendingPaymentStop?.pricing_tier_id;
                        const tierPrice = customerTierId && a.tier_prices
                          ? a.tier_prices.find(tp => tp.pricing_tier_id === customerTierId)?.price
                          : undefined;
                        return tierPrice !== undefined ? tierPrice : parseFloat(a.price);
                      })
                      .reduce((sum, p) => sum + p, 0)
                      .toFixed(2)}
                  </div>
                )}
              </div>
            )}

            {/* Discount and Extra Charge Section */}
            <div className="adjustments-section">
              {/* Discount */}
              <div className="adjustment-item">
                {!showDiscountForm ? (
                  <button
                    className="add-adjustment-btn add-discount-btn"
                    onClick={() => setShowDiscountForm(true)}
                  >
                    + Add Discount
                  </button>
                ) : (
                  <div className="adjustment-form discount-form">
                    <div className="adjustment-form-header">
                      <span className="adjustment-label">Discount</span>
                      <button
                        className="remove-adjustment-btn"
                        onClick={() => {
                          setShowDiscountForm(false);
                          setStopDiscount({ amount: '', type: '', reason: '', ongoing: false });
                        }}
                      >
                        ✕
                      </button>
                    </div>
                    <div className="discount-form-row">
                      <select
                        className="discount-type-select"
                        value={stopDiscount.type}
                        onChange={(e) => setStopDiscount({ ...stopDiscount, type: e.target.value })}
                      >
                        <option value="">Type</option>
                        <option value="dollar">$ Dollar</option>
                        <option value="percent">% Percent</option>
                      </select>
                      <input
                        type="number"
                        className="discount-amount-input"
                        placeholder="Amount"
                        value={stopDiscount.amount}
                        onChange={(e) => setStopDiscount({ ...stopDiscount, amount: e.target.value })}
                        step={stopDiscount.type === 'percent' ? '1' : '0.01'}
                        min="0"
                      />
                    </div>
                    <input
                      type="text"
                      className="adjustment-reason-input"
                      placeholder="Reason (optional)"
                      value={stopDiscount.reason}
                      onChange={(e) => setStopDiscount({ ...stopDiscount, reason: e.target.value })}
                    />
                    {/* Apply to all future visits checkbox - only show for new completions */}
                    {!isEditingPayment && (
                      <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginTop: '10px' }}>
                        <input
                          type="checkbox"
                          checked={stopDiscount.ongoing}
                          onChange={(e) => setStopDiscount({ ...stopDiscount, ongoing: e.target.checked })}
                          style={{ width: '18px', height: '18px', accentColor: '#2563eb', cursor: 'pointer' }}
                        />
                        <span style={{ fontSize: '0.85rem', color: '#64748b' }}>Apply to all future visits</span>
                      </label>
                    )}
                    {isEditingPayment && (
                      <button
                        type="button"
                        className="remove-discount-link"
                        onClick={() => {
                          setShowDiscountForm(false);
                          setStopDiscount({ amount: '', type: '', reason: '', ongoing: false });
                        }}
                      >
                        Remove Discount
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Extra Charge */}
              <div className="adjustment-item">
                {!showExtraChargeForm ? (
                  <button
                    className="add-adjustment-btn add-extra-charge-btn"
                    onClick={() => setShowExtraChargeForm(true)}
                  >
                    + Add Extra Charge
                  </button>
                ) : (
                  <div className="adjustment-form extra-charge-form">
                    <div className="adjustment-form-header">
                      <span className="adjustment-label">Extra Charge</span>
                      <button
                        className="remove-adjustment-btn"
                        onClick={() => {
                          setShowExtraChargeForm(false);
                          setExtraCharge({ amount: '', reason: '' });
                        }}
                      >
                        ✕
                      </button>
                    </div>
                    <div className="extra-charge-form-row">
                      <span className="extra-charge-prefix">$</span>
                      <input
                        type="number"
                        className="extra-charge-amount-input"
                        placeholder="0.00"
                        value={extraCharge.amount}
                        onChange={(e) => setExtraCharge({ ...extraCharge, amount: e.target.value })}
                        step="0.01"
                        min="0"
                      />
                    </div>
                    <input
                      type="text"
                      className="adjustment-reason-input"
                      placeholder="Reason (e.g., heavy snow, tip)"
                      value={extraCharge.reason}
                      onChange={(e) => setExtraCharge({ ...extraCharge, reason: e.target.value })}
                    />
                  </div>
                )}
              </div>

              {/* Comp/Free Toggle */}
              <div className="adjustment-item comp-toggle-container">
                <label className="comp-toggle-label">
                  <input
                    type="checkbox"
                    checked={isComp}
                    onChange={(e) => setIsComp(e.target.checked)}
                    className="comp-toggle-checkbox"
                  />
                  <span className="comp-toggle-text">Mark as Comp/Free</span>
                </label>
                {isComp && (
                  <input
                    type="text"
                    className="adjustment-reason-input comp-reason-input"
                    placeholder="Reason for comp (optional)"
                    value={compReason}
                    onChange={(e) => setCompReason(e.target.value)}
                  />
                )}
              </div>

              {/* Photo Upload Section */}
              <div className="adjustment-item photo-upload-section">
                <div className="photo-upload-header">
                  <span className="photo-section-label">📷 Photos</span>
                  <button
                    type="button"
                    className="add-photo-btn"
                    onClick={() => photoInputRef.current?.click()}
                    disabled={photoUploading}
                  >
                    {photoUploading ? 'Uploading...' : '+ Add Photo'}
                  </button>
                  <input
                    type="file"
                    ref={photoInputRef}
                    accept="image/*"
                    capture="environment"
                    onChange={handlePhotoUpload}
                    style={{ display: 'none' }}
                  />
                </div>
                {stopPhotos.length > 0 && (
                  <div className="photo-thumbnails">
                    {stopPhotos.map(photo => (
                      <div key={photo.id} className="photo-thumbnail-container">
                        <img
                          src={photo.photo_url}
                          alt="Stop"
                          className="photo-thumbnail"
                          onClick={() => setSelectedPhoto(photo)}
                        />
                        <button
                          className="delete-photo-btn"
                          onClick={() => handleDeletePhoto(photo.id)}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Save Changes button - only when editing completed stop */}
            {isEditingPayment && (
              <div className="save-adjustments-section">
                <button
                  className="save-adjustments-btn"
                  onClick={handleSaveAdjustments}
                  disabled={completing}
                >
                  {completing ? 'Saving...' : 'Save Changes'}
                </button>
                <p className="save-adjustments-hint">
                  Save discount, extra charge, or comp changes without changing payment method
                </p>
              </div>
            )}

            {/* Comp Button - shown when isComp is checked */}
            {isComp && (
              <div className="payment-options comp-submit-section">
                <button
                  className="payment-option-btn comp-submit-btn"
                  onClick={() => handlePaymentSelection('comp')}
                >
                  <span className="payment-icon">✓</span>
                  <span>Mark as Comp - Complete Stop</span>
                </button>
              </div>
            )}

            {/* Manual Payment Methods - Paid/Not Paid pairs (hidden when comp is checked) */}
            {!isComp && (
            <>
            <div className="payment-options payment-options-grid">
              {/* Venmo options */}
              {enabledPaymentMethods.some(m => m.id === 'venmo') && (
                <>
                  <button
                    className="payment-option-btn venmo-paid"
                    onClick={() => handlePaymentSelection('venmo_paid')}
                  >
                    <span className="payment-icon">V</span>
                    <span>Venmo - Paid</span>
                  </button>
                  <button
                    className="payment-option-btn venmo-not-paid"
                    onClick={() => handlePaymentSelection('venmo_not_paid')}
                  >
                    <span className="payment-icon">V</span>
                    <span>Venmo - Not Paid</span>
                  </button>
                </>
              )}
              {/* Cash App options */}
              {enabledPaymentMethods.some(m => m.id === 'cashapp') && (
                <>
                  <button
                    className="payment-option-btn cashapp-paid"
                    onClick={() => handlePaymentSelection('cashapp_paid')}
                  >
                    <span className="payment-icon">$</span>
                    <span>Cash App - Paid</span>
                  </button>
                  <button
                    className="payment-option-btn cashapp-not-paid"
                    onClick={() => handlePaymentSelection('cashapp_not_paid')}
                  >
                    <span className="payment-icon">$</span>
                    <span>Cash App - Not Paid</span>
                  </button>
                </>
              )}
              {/* Zelle options */}
              {enabledPaymentMethods.some(m => m.id === 'zelle') && (
                <>
                  <button
                    className="payment-option-btn zelle-paid"
                    onClick={() => handlePaymentSelection('zelle_paid')}
                  >
                    <span className="payment-icon">Z</span>
                    <span>Zelle - Paid</span>
                  </button>
                  <button
                    className="payment-option-btn zelle-not-paid"
                    onClick={() => handlePaymentSelection('zelle_not_paid')}
                  >
                    <span className="payment-icon">Z</span>
                    <span>Zelle - Not Paid</span>
                  </button>
                </>
              )}
              {/* Check options */}
              {enabledPaymentMethods.some(m => m.id === 'check') && (
                <>
                  <button
                    className="payment-option-btn check-paid"
                    onClick={() => handlePaymentSelection('check_paid')}
                  >
                    <span className="payment-icon">✓</span>
                    <span>Check - Paid</span>
                  </button>
                  <button
                    className="payment-option-btn check-not-paid"
                    onClick={() => handlePaymentSelection('check_not_paid')}
                  >
                    <span className="payment-icon">✓</span>
                    <span>Check - Not Paid</span>
                  </button>
                </>
              )}
              {/* Cash option - always paid */}
              {enabledPaymentMethods.some(m => m.id === 'cash') && (
                <button
                  className="payment-option-btn cash-paid"
                  onClick={() => handlePaymentSelection('cash_paid')}
                >
                  <span className="payment-icon">$</span>
                  <span>Cash</span>
                </button>
              )}
              {/* Not Paid option */}
              <button
                className="payment-option-btn not-paid"
                onClick={() => handlePaymentSelection('not_paid')}
              >
                <span className="payment-icon">✕</span>
                <span>Not Paid</span>
              </button>
            </div>

            {/* Invoice-based payment processors section */}
            {enabledPaymentMethods.some(m => m.id === 'square') && (
              <>
                <div className="payment-section-divider">
                  <span>Send Invoice</span>
                </div>
                <div className="payment-options invoice-payment-options">
                  <button
                    className="payment-option-btn square"
                    onClick={() => handlePaymentSelection('square')}
                  >
                    <span className="payment-icon">□</span>
                    <span>Square</span>
                  </button>
                </div>
              </>
            )}
            </>
            )}
          </div>
        </div>
      )}

      {/* Add Customer Modal */}
      {showAddCustomer && (
        <div className="modal-overlay" onClick={() => { setShowAddCustomer(false); setShowNewCustomerForm(false); }}>
          <div className="add-customer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{showNewCustomerForm ? 'New Service Call' : 'Add Customer to Route'}</h2>
              <button className="modal-close" onClick={() => { setShowAddCustomer(false); setShowNewCustomerForm(false); }}>×</button>
            </div>
            <div className="modal-body">
              {!showNewCustomerForm ? (
                <>
                  {/* Add New Customer Button - Hidden for drivers and crew leads */}
                  {isAdmin() && (
                    <>
                      <button
                        className="new-customer-btn"
                        onClick={() => setShowNewCustomerForm(true)}
                      >
                        + Add New Customer (Service Call)
                      </button>

                      <div className="modal-divider">
                        <span>or select existing</span>
                      </div>
                    </>
                  )}

                  <input
                    type="text"
                    className="customer-search-input"
                    placeholder="Search customers..."
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    autoFocus
                  />
                  <div className="customer-list">
                    {filteredCustomers.length === 0 ? (
                      <div className="no-customers">
                        {availableCustomers.length === 0
                          ? 'All customers are already on this route'
                          : 'No customers match your search'}
                      </div>
                    ) : (
                      filteredCustomers.map((customer) => (
                        <button
                          key={customer.id}
                          className="customer-select-item"
                          onClick={() => addCustomerMidRoute(customer)}
                          disabled={addingCustomer}
                        >
                          <div className="customer-info">
                            <div className="customer-name">
                              {customer.first_name} {customer.last_name}
                            </div>
                            <div className="customer-address">
                              {customer.address}, {customer.city}
                            </div>
                          </div>
                          <div className="add-indicator">
                            {addingCustomer ? '...' : '+'}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </>
              ) : (
                /* New Customer Form */
                <div className="new-customer-form">
                  <button
                    className="back-btn"
                    onClick={() => setShowNewCustomerForm(false)}
                  >
                    Back to Search
                  </button>

                  <div className="form-row">
                    <input
                      type="text"
                      placeholder="First Name *"
                      value={newCustomer.firstName}
                      onChange={(e) => setNewCustomer({ ...newCustomer, firstName: e.target.value })}
                      autoFocus
                    />
                    <input
                      type="text"
                      placeholder="Last Name"
                      value={newCustomer.lastName}
                      onChange={(e) => setNewCustomer({ ...newCustomer, lastName: e.target.value })}
                    />
                  </div>

                  <input
                    type="text"
                    placeholder="Street Address *"
                    value={newCustomer.address}
                    onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
                  />

                  <div className="form-row form-row-3">
                    <input
                      type="text"
                      placeholder="City *"
                      value={newCustomer.city}
                      onChange={(e) => setNewCustomer({ ...newCustomer, city: e.target.value })}
                    />
                    <input
                      type="text"
                      placeholder="State *"
                      value={newCustomer.state}
                      onChange={(e) => setNewCustomer({ ...newCustomer, state: e.target.value.toUpperCase() })}
                      maxLength={2}
                      style={{ maxWidth: '70px' }}
                    />
                    <input
                      type="text"
                      placeholder="Zip *"
                      value={newCustomer.zip}
                      onChange={(e) => setNewCustomer({ ...newCustomer, zip: e.target.value })}
                      maxLength={10}
                      style={{ maxWidth: '100px' }}
                    />
                  </div>

                  <div className="form-row">
                    <input
                      type="tel"
                      placeholder="Phone"
                      value={newCustomer.phone}
                      onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                    />
                    <input
                      type="email"
                      placeholder="Email"
                      value={newCustomer.email}
                      onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                    />
                  </div>

                  <div className="form-section-title">Service Details</div>

                  <div className="form-row">
                    <div className="form-field">
                      <label className="form-field-label">Tier</label>
                      <select
                        value={newCustomer.tier}
                        onChange={(e) => handleTierChange(e.target.value)}
                      >
                        {pricingTiers.map(tier => (
                          <option key={tier.id} value={String(tier.id)}>
                            {tier.name} - ${parseFloat(tier.default_price).toFixed(2)}
                          </option>
                        ))}
                        <option value="custom">Custom</option>
                      </select>
                    </div>
                    <div className="form-field">
                      <label className="form-field-label">Payment Method</label>
                      <select
                        value={newCustomer.paymentMethodPreferred}
                        onChange={(e) => setNewCustomer({ ...newCustomer, paymentMethodPreferred: e.target.value })}
                      >
                        {enabledPaymentMethods.map(method => (
                          <option key={method.id} value={method.id}>{method.name}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="form-row form-row-3">
                    <div className="form-field">
                      <label className="form-field-label">Base Price *</label>
                      <div className="price-input-wrapper">
                        <span className="price-prefix">$</span>
                        <input
                          type="number"
                          value={newCustomer.priceSnow}
                          onChange={(e) => setNewCustomer({ ...newCustomer, priceSnow: e.target.value, tier: 'custom' })}
                          step="0.01"
                          min="0"
                        />
                      </div>
                    </div>
                    <div className="form-field">
                      <label className="form-field-label">Fertilizer Price</label>
                      <div className="price-input-wrapper">
                        <span className="price-prefix">$</span>
                        <input
                          type="number"
                          value={newCustomer.saltPrice}
                          onChange={(e) => setNewCustomer({ ...newCustomer, saltPrice: e.target.value, tier: 'custom' })}
                          step="0.01"
                          min="0"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">Service Preference</label>
                    <select
                      value={newCustomer.serviceFrequency}
                      onChange={(e) => setNewCustomer({ ...newCustomer, serviceFrequency: e.target.value })}
                    >
                      <option value="seasonal">Seasonal (contracted for the season)</option>
                      <option value="as_needed">On-Call / As Needed</option>
                    </select>
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">How did you find me?</label>
                    <select
                      value={newCustomer.leadSource}
                      onChange={(e) => setNewCustomer({ ...newCustomer, leadSource: e.target.value })}
                    >
                      <option value="">-- Select --</option>
                      {enabledLeadSources.map(source => (
                        <option key={source.id} value={source.id}>{source.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">Services Included</label>
                    <div className="checkbox-grid">
                      <label className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={newCustomer.driveway}
                          onChange={(e) => setNewCustomer({ ...newCustomer, driveway: e.target.checked })}
                        />
                        <span>Mowing</span>
                      </label>
                      <label className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={newCustomer.sidewalks}
                          onChange={(e) => setNewCustomer({ ...newCustomer, sidewalks: e.target.checked })}
                        />
                        <span>Edging</span>
                      </label>
                      <label className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={newCustomer.walkways}
                          onChange={(e) => setNewCustomer({ ...newCustomer, walkways: e.target.checked })}
                        />
                        <span>Trimming</span>
                      </label>
                      <label className="checkbox-item">
                        <input
                          type="checkbox"
                          checked={newCustomer.salt}
                          onChange={(e) => setNewCustomer({ ...newCustomer, salt: e.target.checked })}
                        />
                        <span>Fertilizer</span>
                      </label>
                    </div>
                  </div>

                  <div className="form-field">
                    <label className="form-field-label">Notes</label>
                    <textarea
                      placeholder="Special instructions, gate codes, etc."
                      value={newCustomer.notes}
                      onChange={(e) => setNewCustomer({ ...newCustomer, notes: e.target.value })}
                      rows={3}
                    />
                  </div>

                  <button
                    className="btn btn-primary create-customer-btn"
                    onClick={createAndAddCustomer}
                    disabled={addingCustomer}
                  >
                    {addingCustomer ? 'Creating...' : 'Create & Add to Route'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Phone Action Popup */}
      {phonePopup.show && (
        <div
          ref={phonePopupRef}
          className="phone-action-popup"
          style={{
            position: 'fixed',
            left: phonePopup.x,
            top: phonePopup.y,
            zIndex: 9999
          }}
        >
          <button
            className="phone-action-option"
            onClick={() => {
              window.location.href = `tel:${phonePopup.phone}`;
              setPhonePopup({ show: false, phone: '', x: 0, y: 0 });
            }}
          >
            <span className="phone-action-icon">📞</span>
            <span>Call</span>
          </button>
          <div className="phone-action-divider"></div>
          <button
            className="phone-action-option"
            onClick={() => {
              window.location.href = `sms:${phonePopup.phone}`;
              setPhonePopup({ show: false, phone: '', x: 0, y: 0 });
            }}
          >
            <span className="phone-action-icon">💬</span>
            <span>Text</span>
          </button>
        </div>
      )}

      {/* Billing Modal */}
      {showBillingModal && (
        <div className="modal-overlay" onClick={() => !billingInProgress && setShowBillingModal(false)}>
          <div className="billing-modal" onClick={e => e.stopPropagation()}>
            <div className="billing-modal-header">
              <h2>Bill Customers</h2>
              <button
                className="modal-close-btn"
                onClick={() => !billingInProgress && setShowBillingModal(false)}
                disabled={billingInProgress}
              >
                ×
              </button>
            </div>

            <div className="billing-modal-content">
              {billingResults ? (
                /* Show results after billing */
                <div className="billing-results">
                  <div className="billing-results-header">
                    <h3>Billing Complete</h3>
                    {billingResults.sent?.length > 0 && (
                      <span className="results-summary success">
                        ✓ {billingResults.sent.length} sent
                      </span>
                    )}
                    {billingResults.failed?.length > 0 && (
                      <span className="results-summary failed">
                        ✗ {billingResults.failed.length} failed
                      </span>
                    )}
                  </div>
                  <div className="billing-results-list">
                    {billingResults.sent?.map((item, i) => (
                      <div key={`sent-${i}`} className="billing-result-item success">
                        <span className="result-icon">✓</span>
                        <span className="result-name">{item.name}</span>
                        <span className="result-amount">${item.amount?.toFixed(2)}</span>
                        <span className="result-status">Invoice sent</span>
                      </div>
                    ))}
                    {billingResults.failed?.map((item, i) => (
                      <div key={`failed-${i}`} className="billing-result-item failed">
                        <span className="result-icon">✗</span>
                        <span className="result-name">{item.name}</span>
                        <span className="result-reason">{item.reason}</span>
                      </div>
                    ))}
                  </div>
                  {billingResults.sent?.length === 0 && billingResults.failed?.length === 0 && (
                    <p className="no-results">No invoices were processed</p>
                  )}
                </div>
              ) : (
                /* Show billing preview */
                (() => {
                  const categories = getBillingCategories();
                  const hasAutoBill = categories.autoBill.length > 0;
                  const hasNoEmail = categories.noEmail.length > 0;
                  const hasManual = categories.manual.length > 0;

                  return (
                    <>
                      {hasAutoBill && (
                        <div className="billing-section">
                          <h3 className="billing-section-title">
                            <span className="billing-icon">💳</span>
                            Square (auto-invoice)
                          </h3>
                          {categories.autoBill.map(stop => (
                            <div key={stop.id} className="billing-item auto-bill">
                              <span className="billing-check">☑</span>
                              <span className="billing-name">{stop.customerName}</span>
                              <span className="billing-amount">${stop.amount.toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {hasNoEmail && (
                        <div className="billing-section">
                          <h3 className="billing-section-title warning">
                            <span className="billing-icon">⚠️</span>
                            Cannot auto-bill (no email)
                          </h3>
                          {categories.noEmail.map(stop => (
                            <div key={stop.id} className="billing-item no-email">
                              <span className="billing-warning">⚠</span>
                              <span className="billing-name">{stop.customerName}</span>
                              <span className="billing-amount">${stop.amount.toFixed(2)}</span>
                              <span className="billing-method">Square</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {hasManual && (
                        <div className="billing-section">
                          <h3 className="billing-section-title manual">
                            <span className="billing-icon">📋</span>
                            Manual billing required
                          </h3>
                          {categories.manual.map(stop => (
                            <div key={stop.id} className="billing-item manual">
                              <span className="billing-manual">📋</span>
                              <span className="billing-name">{stop.customerName}</span>
                              <span className="billing-amount">${stop.amount.toFixed(2)}</span>
                              <span className="billing-method">{stop.payment_method}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {!hasAutoBill && !hasNoEmail && !hasManual && (
                        <p className="no-billable">No unpaid customers to bill</p>
                      )}
                    </>
                  );
                })()
              )}
            </div>

            <div className="billing-modal-footer">
              {billingResults ? (
                <button
                  className="btn btn-primary done-btn"
                  onClick={() => {
                    setShowBillingModal(false);
                    setBillingResults(null);
                  }}
                >
                  Done
                </button>
              ) : (
                <>
                  <button
                    className="btn btn-outline"
                    onClick={() => setShowBillingModal(false)}
                    disabled={billingInProgress}
                  >
                    Cancel
                  </button>
                  {getBillingCategories().autoBill.length > 0 && (
                    <button
                      className="btn btn-primary"
                      onClick={handleBillAll}
                      disabled={billingInProgress}
                    >
                      {billingInProgress ? 'Sending...' : `Send ${getBillingCategories().autoBill.length} Invoice${getBillingCategories().autoBill.length === 1 ? '' : 's'}`}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Photo Preview Modal */}
      {selectedPhoto && (
        <div className="modal-overlay photo-preview-overlay" onClick={() => setSelectedPhoto(null)}>
          <div className="photo-preview-modal" onClick={(e) => e.stopPropagation()}>
            <button className="photo-preview-close" onClick={() => setSelectedPhoto(null)}>✕</button>
            <img src={selectedPhoto.photo_url} alt="Full size" className="photo-preview-image" />
            {selectedPhoto.caption && <p className="photo-preview-caption">{selectedPhoto.caption}</p>}
          </div>
        </div>
      )}

      <style>{`
        .storm-header {
          background: linear-gradient(135deg, var(--color-secondary) 0%, var(--color-secondary-dark) 100%);
        }

        .header-nav {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 8px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.15s ease;
        }
        .nav-link:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .nav-link.active {
          background: rgba(255,255,255,0.2);
          color: white;
        }
        .storm-active {
          background: rgba(0,0,0,0.2) !important;
        }
        .admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%) !important;
          color: white !important;
        }
        .admin-link:hover {
          background: linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%) !important;
        }
        .logout-link {
          margin-left: 8px;
          border-left: 1px solid rgba(255,255,255,0.2);
          padding-left: 16px;
        }

        .storm-container {
          max-width: 800px;
        }

        .storm-title {
          font-size: 2rem;
          font-weight: 700;
          margin: 0 0 8px 0;
          color: var(--color-secondary);
        }
        .storm-subtitle {
          color: var(--color-text-light);
          margin: 0 0 16px 0;
        }

        /* Route Controls (Filter + Sort) */
        .route-controls {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 20px;
        }
        .sort-toggle {
          padding: 8px 14px;
          border: 2px solid var(--color-border);
          border-radius: 20px;
          background: var(--color-card);
          cursor: pointer;
          font-size: 0.85rem;
          font-weight: 500;
          color: var(--color-text-light);
          transition: all 0.2s ease;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .sort-toggle:hover {
          border-color: var(--color-primary);
          color: var(--color-primary);
        }

        /* Route Filter Tabs */
        .route-filter-tabs {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .filter-tab {
          padding: 10px 20px;
          border: 2px solid var(--color-border);
          border-radius: 25px;
          background: var(--color-card);
          cursor: pointer;
          font-size: 0.9rem;
          font-weight: 500;
          transition: all 0.2s ease;
        }
        .filter-tab:hover {
          border-color: var(--color-secondary);
        }
        .filter-tab.active {
          background: var(--color-secondary);
          border-color: var(--color-secondary);
          color: white;
        }
        .empty-filter-state {
          text-align: center;
          padding: 32px 16px;
          color: var(--color-text-light);
        }

        /* Route Status Badges in Header */
        .route-status-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
          margin-left: 8px;
        }
        .route-status-badge.completed {
          background: #d1fae5;
          color: #059669;
        }
        .route-status-badge.archived {
          background: #e5e7eb;
          color: #6b7280;
        }

        .empty-state {
          text-align: center;
          padding: 48px 16px;
          color: var(--color-text-light);
        }
        .empty-state .btn {
          margin-top: 16px;
        }

        /* Route Selection */
        .route-select-grid {
          display: grid;
          gap: 16px;
        }
        .route-select-card {
          background: var(--color-card);
          border: 2px solid var(--color-border);
          border-radius: 16px;
          padding: 20px;
          text-align: left;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .route-select-card:hover {
          border-color: var(--color-secondary);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        .route-select-card.completed {
          opacity: 0.85;
          border-color: #10b981;
        }
        .route-select-card.archived {
          opacity: 0.6;
          border-style: dashed;
        }
        .route-select-name {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 8px;
        }
        .route-select-meta {
          display: flex;
          gap: 12px;
          align-items: center;
          color: var(--color-text-light);
          font-size: 0.875rem;
          flex-wrap: wrap;
        }

        .route-total {
          font-size: 1rem;
          font-weight: 700;
          color: var(--color-success);
          background: rgba(76, 175, 80, 0.1);
          padding: 4px 10px;
          border-radius: 6px;
        }
        .driver-pay-badge {
          font-size: 0.9rem;
          font-weight: 600;
          color: #2196F3;
          background: rgba(33, 150, 243, 0.1);
          padding: 4px 10px;
          border-radius: 6px;
        }
        .assigned-badge {
          font-size: 0.75rem;
          background: #8b5cf6;
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          font-weight: 500;
        }
        .assigned-badge.unassigned {
          background: #6b7280;
        }

        /* Payment breakdown styles */
        .payment-breakdown {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 0.8rem;
          padding: 3px 8px;
          border-radius: 4px;
          background: rgba(0, 0, 0, 0.03);
        }
        .payment-fully-paid {
          color: #10b981;
          font-weight: 600;
        }
        .payment-paid {
          color: #10b981;
        }
        .payment-unpaid {
          color: #f59e0b;
        }
        .payment-separator {
          color: #d1d5db;
          margin: 0 2px;
        }

        .status-badge {
          font-size: 0.75rem;
          padding: 4px 10px;
          border-radius: 12px;
          font-weight: 500;
        }
        .status-badge.in-progress {
          background: #fef3c7;
          color: #92400e;
        }
        .status-badge.completed {
          background: #d1fae5;
          color: #065f46;
        }
        .status-badge.not-started {
          background: #f3f4f6;
          color: #6b7280;
        }
        .status-badge.archived {
          background: #e5e7eb;
          color: #6b7280;
        }

        /* Active Route */
        .route-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 16px;
        }
        .btn-back {
          flex-shrink: 0;
        }
        .route-header-info {
          flex: 1;
        }
        .route-title {
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0;
        }
        .progress-info {
          color: var(--color-text-light);
          font-size: 0.875rem;
        }
        .route-header-actions {
          display: flex;
          gap: 8px;
          flex-shrink: 0;
        }

        .progress-bar-container {
          height: 8px;
          background: var(--color-border);
          border-radius: 4px;
          margin-bottom: 24px;
          overflow: hidden;
        }
        .progress-bar-fill {
          height: 100%;
          background: var(--color-secondary);
          border-radius: 4px;
          transition: width 0.3s ease;
        }

        .current-location-bar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: var(--color-background);
          border: 1px solid var(--color-border);
          border-radius: 10px;
          padding: 12px 16px;
          margin-bottom: 20px;
          gap: 12px;
          flex-wrap: wrap;
        }
        .current-location-info {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
          flex: 1;
          min-width: 200px;
        }
        .current-location-label {
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          color: var(--color-text-light);
        }
        .current-location-name {
          font-weight: 600;
          color: var(--color-secondary);
        }
        .current-location-address {
          font-size: 0.875rem;
          color: var(--color-text-light);
        }
        .reoptimize-container {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }
        .gps-status {
          font-size: 0.8rem;
          padding: 4px 10px;
          border-radius: 6px;
          white-space: nowrap;
        }
        .gps-status-acquiring {
          background: #dbeafe;
          color: #1d4ed8;
        }
        .gps-status-success {
          background: #dcfce7;
          color: #166534;
        }
        .gps-status-unavailable {
          background: #fef3c7;
          color: #92400e;
        }

        .stops-section {
          margin-bottom: 32px;
        }
        .stops-section-title {
          font-size: 1rem;
          font-weight: 600;
          margin: 0 0 16px 0;
          color: var(--color-text-light);
        }

        .storm-stops-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .storm-stop-card {
          background: var(--color-card);
          border-radius: 16px;
          padding: 20px;
          display: flex;
          gap: 16px;
          align-items: flex-start;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
        .storm-stop-card.next-up {
          border: 2px solid var(--color-secondary);
          background: linear-gradient(135deg, rgba(255,152,0,0.05) 0%, rgba(255,152,0,0.02) 100%);
        }

        .stop-order-badge {
          width: 40px;
          height: 40px;
          background: var(--color-secondary);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 1.125rem;
          flex-shrink: 0;
        }

        .stop-details {
          flex: 1;
        }
        .stop-customer-name {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 4px;
        }
        .stop-address {
          color: var(--color-text);
        }
        .stop-city {
          color: var(--color-text-light);
          font-size: 0.875rem;
          margin-bottom: 8px;
        }
        .stop-phone {
          color: var(--color-primary);
          font-size: 0.875rem;
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          text-decoration: underline;
          font-family: inherit;
        }
        .stop-phone:hover {
          color: var(--color-primary-dark);
        }
        .phone-action-popup {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          overflow: hidden;
          min-width: 140px;
        }
        .phone-action-option {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          padding: 14px 18px;
          background: none;
          border: none;
          cursor: pointer;
          font-size: 1rem;
          font-family: inherit;
          color: var(--color-text);
          transition: background 0.15s ease;
        }
        .phone-action-option:hover {
          background: #f3f4f6;
        }
        .phone-action-option:active {
          background: #e5e7eb;
        }
        .phone-action-icon {
          font-size: 1.25rem;
        }
        .phone-action-divider {
          height: 1px;
          background: var(--color-border);
        }
        .stop-price {
          margin-top: 8px;
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .stop-price .regular-price {
          font-weight: 600;
          color: var(--color-text);
          font-size: 1rem;
        }
        .stop-price .original-price {
          text-decoration: line-through;
          color: #9ca3af;
          font-size: 0.875rem;
        }
        .stop-price .discounted-price {
          font-weight: 700;
          color: #10b981;
          font-size: 1.1rem;
        }
        .referral-discount-badge {
          background: #10b981;
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 500;
        }
        .salt-badge {
          background: #d1fae5;
          color: #059669;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 0.7rem;
          font-weight: 600;
        }
        .extra-charge-badge {
          background: #fef3c7;
          color: #d97706;
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 0.7rem;
          font-weight: 600;
        }
        .extra-charge-indicator {
          background: #fef3c7;
          color: #d97706;
          padding: 1px 5px;
          border-radius: 3px;
          font-size: 0.7rem;
          font-weight: 600;
          margin-left: 6px;
        }
        .service-frequency-badge {
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 0.65rem;
          font-weight: 600;
          margin-left: 8px;
          white-space: nowrap;
        }
        .service-frequency-badge.seasonal {
          background: rgba(59, 130, 246, 0.15);
          color: #1d4ed8;
        }
        .service-frequency-badge.as-needed {
          background: rgba(249, 115, 22, 0.15);
          color: #c2410c;
        }
        .payment-method-badge {
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 0.7rem;
          font-weight: 600;
        }
        .payment-method-badge.cash {
          background: #dcfce7;
          color: #16a34a;
        }
        .payment-method-badge.venmo {
          background: #dbeafe;
          color: #2563eb;
        }
        .payment-method-badge.square {
          background: #e0e7ff;
          color: #4338ca;
        }
        .payment-method-badge.cashapp {
          background: #d1fae5;
          color: #059669;
        }
        .stop-notes {
          margin-top: 8px;
          font-size: 0.85rem;
          color: #6b7280;
          font-style: italic;
          padding: 6px 10px;
          background: #f9fafb;
          border-left: 3px solid #d1d5db;
          border-radius: 0 4px 4px 0;
        }
        .stop-discount-reason {
          font-size: 0.8rem;
          color: #059669;
          font-style: italic;
          margin-top: 4px;
        }
        .discount-badge {
          background: rgba(5, 150, 105, 0.15);
          color: #059669;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.7rem;
          font-weight: 600;
        }

        /* Completed stops display */
        .completed-info {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-width: 0;
        }
        .completed-discount-info {
          font-size: 0.7rem;
          color: #059669;
          font-style: italic;
          margin-top: 2px;
        }
        .completed-stop-item.has-discount {
          background: rgba(5, 150, 105, 0.03);
        }
        .completed-price {
          font-size: 0.85rem;
          margin-left: auto;
          margin-right: 12px;
          color: var(--color-text);
          text-align: right;
          flex-shrink: 0;
        }
        .completed-price .price-strike {
          text-decoration: line-through;
          color: #9ca3af;
          margin-right: 4px;
        }
        .completed-price .price-discounted {
          color: #059669;
          font-weight: 600;
        }
        .completed-price .comp-badge {
          background: #dbeafe;
          color: #1d4ed8;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .stop-actions {
          display: flex;
          flex-direction: column;
          gap: 8px;
          align-items: center;
        }
        .stop-complete-btn {
          min-width: 80px;
          font-size: 1.125rem;
          padding: 16px 24px;
        }
        .stop-secondary-actions {
          display: flex;
          gap: 8px;
        }

        /* Completed Stops */
        .completed-section {
          opacity: 0.7;
        }
        .completed-stops-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .completed-stop-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: var(--color-background);
          border-radius: 8px;
          transition: all 0.15s ease;
        }
        .completed-stop-item.editable {
          cursor: pointer;
        }
        .completed-stop-item.editable:hover {
          background: var(--color-card);
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .completed-stop-item.editable:active {
          transform: scale(0.98);
        }
        .completed-icon {
          width: 24px;
          height: 24px;
          background: #10b981;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.875rem;
          flex-shrink: 0;
        }
        .completed-icon.skipped {
          background: #9ca3af;
        }
        .completed-name {
          font-weight: 500;
          flex-shrink: 0;
        }
        .completed-address {
          color: var(--color-text-light);
          font-size: 0.875rem;
          flex: 1;
          text-align: right;
        }

        /* Completed Route View */
        .completed-route-view {
          padding: 0;
        }
        .route-complete-banner {
          background: linear-gradient(135deg, #10b981, #059669);
          color: white;
          padding: 16px 20px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }
        .complete-check {
          background: rgba(255,255,255,0.2);
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
        }
        .route-total-banner {
          margin-left: auto;
          font-weight: 700;
          font-size: 1.1rem;
        }
        .payment-summary-bar {
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--color-card);
          border: 2px solid var(--color-border);
          border-radius: 12px;
          padding: 12px 16px;
          margin-bottom: 16px;
          gap: 0;
        }
        .payment-summary-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 0 20px;
          flex: 1;
        }
        .payment-summary-label {
          font-size: 0.75rem;
          font-weight: 500;
          color: var(--color-text-light);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
        }
        .payment-summary-amount {
          font-size: 1.1rem;
          font-weight: 700;
        }
        .payment-summary-item.paid .payment-summary-amount {
          color: #10b981;
        }
        .payment-summary-item.unpaid .payment-summary-amount {
          color: #f59e0b;
        }
        .payment-summary-item.total .payment-summary-amount {
          color: var(--color-text);
        }
        .payment-summary-divider {
          width: 1px;
          height: 36px;
          background: var(--color-border);
        }
        .complete-actions-bar {
          display: flex;
          gap: 12px;
          margin-bottom: 20px;
        }
        .completed-stops-detail-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .completed-stop-card {
          background: var(--color-card);
          border: 2px solid var(--color-border);
          border-radius: 12px;
          padding: 16px;
          display: flex;
          gap: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .completed-stop-card:hover {
          border-color: var(--color-primary);
        }
        .completed-stop-card.skipped {
          opacity: 0.6;
          cursor: default;
          border-style: dashed;
        }
        .completed-stop-card.skipped:hover {
          border-color: var(--color-border);
        }
        .skipped-label {
          background: #e5e7eb;
          color: #6b7280;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          margin-left: 8px;
        }
        .stop-payment-status {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 4px;
          min-width: 100px;
        }
        .tap-to-edit {
          font-size: 0.7rem;
          color: var(--color-text-light);
        }

        /* Legacy Route Complete (keeping for compatibility) */
        .route-complete-message {
          text-align: center;
          padding: 48px 16px;
        }
        .complete-icon {
          width: 80px;
          height: 80px;
          background: #10b981;
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 2.5rem;
          margin: 0 auto 24px;
        }
        .route-complete-message h2 {
          font-size: 1.75rem;
          margin: 0 0 8px 0;
          color: #10b981;
        }
        .route-complete-message p {
          color: var(--color-text-light);
          margin: 0 0 24px 0;
        }

        /* Mobile Menu */
        .mobile-menu-btn {
          display: none;
          flex-direction: column;
          justify-content: space-between;
          width: 24px;
          height: 18px;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 0;
        }
        .mobile-menu-btn span {
          display: block;
          width: 100%;
          height: 2px;
          background: white;
          border-radius: 1px;
        }
        .mobile-nav {
          display: flex;
          flex-direction: column;
          padding: 8px 16px 16px;
          border-top: 1px solid rgba(255,255,255,0.1);
        }
        .mobile-nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 12px 8px;
          text-align: left;
          font-size: 1rem;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s ease;
        }
        .mobile-nav-link:hover, .mobile-nav-link.active {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .mobile-nav-link.storm-active {
          background: rgba(0,0,0,0.2);
        }
        .mobile-nav-link.admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.logout-link {
          margin-top: 8px;
          border-top: 1px solid rgba(255,255,255,0.1);
          padding-top: 16px;
        }

        @media (max-width: 768px) {
          .desktop-nav {
            display: none;
          }
          .mobile-menu-btn {
            display: flex;
          }
          .storm-stop-card {
            flex-direction: column;
          }
          .stop-order-badge {
            align-self: flex-start;
          }
          .stop-actions {
            flex-direction: row;
            width: 100%;
          }
          .stop-complete-btn {
            flex: 1;
          }
          .route-header {
            flex-wrap: wrap;
          }
        }

        /* Route Complete Actions */
        .total-amount {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--color-text);
          margin-bottom: 24px !important;
        }
        .complete-actions {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }

        /* Print View Styles */
        .print-view {
          display: none;
        }

        /* Print Media Query */
        @media print {
          /* Hide everything except print view */
          .service-mode-page > *:not(.print-view) {
            display: none !important;
          }

          .print-view {
            display: block !important;
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            background: white;
            padding: 20px;
            box-sizing: border-box;
          }

          .print-header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #333;
            padding-bottom: 20px;
          }
          .print-header h1 {
            margin: 0 0 10px 0;
            font-size: 24px;
            color: #333;
          }
          .print-meta {
            display: flex;
            justify-content: center;
            gap: 40px;
          }
          .print-meta p {
            margin: 5px 0;
            font-size: 14px;
          }

          .print-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          .print-table th,
          .print-table td {
            padding: 10px 12px;
            text-align: left;
            border-bottom: 1px solid #ddd;
          }
          .print-table th {
            background: #f5f5f5;
            font-weight: 600;
            border-bottom: 2px solid #333;
          }
          .print-table tbody tr:nth-child(even) {
            background: #fafafa;
          }
          .print-table tfoot td {
            border-top: 2px solid #333;
            font-size: 16px;
            padding-top: 15px;
          }
          .text-right {
            text-align: right;
          }

          .print-footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            text-align: center;
            font-size: 12px;
            color: #666;
          }
          .print-footer p {
            margin: 5px 0;
          }

          .print-expenses-section {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 2px solid #e5e7eb;
          }
          .print-expenses-section h3 {
            font-size: 14px;
            margin-bottom: 10px;
            color: #374151;
          }
          .expenses-table {
            font-size: 11px;
          }
          .expenses-table td {
            padding: 6px 8px;
          }

          .print-profit-summary {
            margin-top: 20px;
            padding: 15px;
            background: #f9fafb;
            border-radius: 8px;
            border: 1px solid #e5e7eb;
          }
          .profit-row {
            display: flex;
            justify-content: space-between;
            padding: 6px 0;
            font-size: 13px;
          }
          .profit-row.expense {
            color: #dc2626;
          }
          .profit-row.expense-header {
            font-weight: 600;
            margin-top: 8px;
            padding-bottom: 4px;
            border-bottom: 1px solid #e5e7eb;
          }
          .profit-row.expense-item {
            padding-left: 12px;
            font-size: 12px;
          }
          .profit-row.expense-item .expense-detail {
            text-transform: capitalize;
            color: #6b7280;
          }
          .profit-row.expense-total {
            padding-left: 12px;
            margin-top: 4px;
            padding-top: 4px;
            border-top: 1px dashed #d1d5db;
            font-weight: 500;
          }
          .profit-row.net {
            border-top: 2px solid #374151;
            margin-top: 8px;
            padding-top: 10px;
            font-size: 15px;
          }
          .profit-amount {
            font-family: monospace;
          }

          .no-print {
            display: none !important;
          }
        }

        /* Screen-only print preview */
        @media screen {
          .print-view {
            display: none;
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: white;
            z-index: 1000;
            overflow: auto;
            padding: 40px;
          }
          .print-view:target,
          .service-mode-page .print-view {
            display: block;
          }

          .print-header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #333;
            padding-bottom: 20px;
          }
          .print-header h1 {
            margin: 0 0 10px 0;
            font-size: 28px;
            color: #333;
          }
          .print-meta {
            display: flex;
            justify-content: center;
            gap: 40px;
          }
          .print-meta p {
            margin: 5px 0;
            font-size: 14px;
          }

          .print-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
          }
          .print-table th,
          .print-table td {
            padding: 12px 16px;
            text-align: left;
            border-bottom: 1px solid #ddd;
          }
          .print-table th {
            background: #f5f5f5;
            font-weight: 600;
            border-bottom: 2px solid #333;
          }
          .print-table tbody tr:nth-child(even) {
            background: #fafafa;
          }
          .print-table tfoot td {
            border-top: 2px solid #333;
            font-size: 18px;
            padding-top: 15px;
          }
          .text-right {
            text-align: right;
          }

          .print-footer {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            text-align: center;
            font-size: 14px;
            color: #666;
          }
          .print-footer p {
            margin: 5px 0;
          }

          .print-close-btn {
            position: fixed;
            top: 20px;
            right: 20px;
            background: #333;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 8px;
            cursor: pointer;
            font-size: 14px;
          }
          .print-close-btn:hover {
            background: #555;
          }
        }

        /* Add Customer Modal */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
          overflow: hidden;
        }
        .add-customer-modal {
          background: var(--color-card);
          border-radius: 16px;
          width: 100%;
          max-width: 500px;
          max-height: 85vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 20px 40px rgba(0,0,0,0.3);
          overflow: hidden;
        }
        .modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 20px;
          border-bottom: 1px solid var(--color-border);
        }
        .modal-header h2 {
          margin: 0;
          font-size: 1.25rem;
        }
        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: var(--color-text-light);
          padding: 4px 8px;
        }
        .modal-close:hover {
          color: var(--color-text);
        }
        .modal-body {
          padding: 20px;
          overflow-y: auto;
          display: flex;
          flex-direction: column;
          flex: 1;
          -webkit-overflow-scrolling: touch;
        }
        .customer-search-input {
          width: 100%;
          padding: 12px 16px;
          border: 2px solid var(--color-border);
          border-radius: 8px;
          font-size: 1rem;
          margin-bottom: 16px;
        }
        .customer-search-input:focus {
          outline: none;
          border-color: var(--color-secondary);
        }
        .customer-list {
          overflow-y: auto;
          max-height: 400px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .no-customers {
          text-align: center;
          padding: 40px 20px;
          color: var(--color-text-light);
        }
        .customer-select-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
          background: var(--color-background);
          border: 2px solid transparent;
          border-radius: 10px;
          cursor: pointer;
          text-align: left;
          transition: all 0.15s ease;
        }
        .customer-select-item:hover {
          border-color: var(--color-secondary);
          background: var(--color-card);
        }
        .customer-select-item:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .customer-info {
          flex: 1;
        }
        .customer-name {
          font-weight: 600;
          margin-bottom: 4px;
        }
        .customer-address {
          font-size: 0.875rem;
          color: var(--color-text-light);
        }
        .add-indicator {
          width: 32px;
          height: 32px;
          background: var(--color-secondary);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
          font-weight: 700;
          flex-shrink: 0;
        }

        /* New Customer Form Styles */
        .new-customer-btn {
          width: 100%;
          padding: 16px;
          background: linear-gradient(135deg, var(--color-success) 0%, #1b7a3d 100%);
          color: white;
          border: none;
          border-radius: 10px;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .new-customer-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(34, 139, 34, 0.3);
        }
        .modal-divider {
          display: flex;
          align-items: center;
          text-align: center;
          margin: 16px 0;
          color: var(--color-text-light);
          font-size: 0.875rem;
        }
        .modal-divider::before,
        .modal-divider::after {
          content: '';
          flex: 1;
          border-bottom: 1px solid var(--color-border);
        }
        .modal-divider span {
          padding: 0 12px;
        }
        .new-customer-form {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .new-customer-form input {
          padding: 12px 16px;
          border: 2px solid var(--color-border);
          border-radius: 8px;
          font-size: 1rem;
          width: 100%;
          box-sizing: border-box;
        }
        .new-customer-form input:focus {
          outline: none;
          border-color: var(--color-secondary);
        }
        .form-row {
          display: flex;
          gap: 12px;
        }
        .form-row input {
          flex: 1;
        }
        .form-row-3 {
          display: flex;
          gap: 12px;
        }
        .form-row-3 input:first-child {
          flex: 2;
        }
        .back-btn {
          background: transparent;
          border: none;
          color: var(--color-secondary);
          padding: 8px 0;
          font-size: 0.875rem;
          cursor: pointer;
          text-align: left;
          margin-bottom: 8px;
        }
        .back-btn:hover {
          text-decoration: underline;
        }
        .create-customer-btn {
          margin-top: 8px;
          padding: 16px;
          font-size: 1rem;
        }
        .form-section-title {
          font-weight: 600;
          color: var(--color-text);
          margin: 12px 0 4px;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--color-border);
          font-size: 0.9rem;
        }
        .form-field {
          display: flex;
          flex-direction: column;
          gap: 4px;
          flex: 1;
        }
        .form-field-label {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--color-text-light);
          text-transform: uppercase;
        }
        .new-customer-form select {
          padding: 12px 16px;
          border: 2px solid var(--color-border);
          border-radius: 8px;
          font-size: 1rem;
          width: 100%;
          box-sizing: border-box;
          background: white;
          cursor: pointer;
        }
        .new-customer-form select:focus {
          outline: none;
          border-color: var(--color-secondary);
        }
        .new-customer-form textarea {
          padding: 12px 16px;
          border: 2px solid var(--color-border);
          border-radius: 8px;
          font-size: 1rem;
          width: 100%;
          box-sizing: border-box;
          font-family: inherit;
          resize: vertical;
        }
        .new-customer-form textarea:focus {
          outline: none;
          border-color: var(--color-secondary);
        }
        .price-input-wrapper {
          display: flex;
          align-items: center;
        }
        .price-prefix {
          background: var(--color-background);
          padding: 12px 12px;
          border: 2px solid var(--color-border);
          border-right: none;
          border-radius: 8px 0 0 8px;
          color: var(--color-text-light);
          font-weight: 500;
        }
        .price-input-wrapper input {
          border-radius: 0 8px 8px 0 !important;
          flex: 1;
        }
        /* Checkbox styles now in index.css */

        /* Start Route Modal */
        .start-route-modal {
          background: var(--color-card);
          border-radius: 16px;
          width: 100%;
          max-width: 400px;
          padding: 24px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.3);
        }
        .start-route-modal-header {
          text-align: center;
          margin-bottom: 20px;
        }
        .start-route-modal-header h2 {
          margin: 0 0 8px;
          font-size: 1.25rem;
          color: var(--color-text);
        }
        .start-route-name {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--color-secondary);
          margin: 0;
        }
        .start-route-modal-body {
          margin-bottom: 24px;
        }
        .start-route-question {
          font-size: 0.95rem;
          color: var(--color-text-light);
          margin: 0 0 16px;
        }
        .start-location-option {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 14px 16px;
          border: 2px solid var(--color-border);
          border-radius: 10px;
          margin-bottom: 12px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .start-location-option:hover {
          border-color: var(--color-primary);
          background: rgba(37, 99, 235, 0.03);
        }
        .start-location-option.selected {
          border-color: var(--color-primary);
          background: rgba(37, 99, 235, 0.08);
        }
        .start-location-option input[type="radio"] {
          width: 20px;
          height: 20px;
          margin-top: 2px;
          accent-color: var(--color-primary);
        }
        .start-location-option .option-content {
          flex: 1;
        }
        .start-location-option .option-label {
          display: block;
          font-weight: 600;
          color: var(--color-text);
          margin-bottom: 4px;
        }
        .start-location-option .option-address {
          display: block;
          font-size: 0.875rem;
          color: var(--color-text-light);
        }
        .start-location-option .option-address.gps-address {
          color: var(--color-primary);
        }
        .start-route-modal-footer {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }
        .start-route-modal-footer .btn {
          min-width: 100px;
        }

        /* Payment Modal */
        .payment-modal {
          background: var(--color-card);
          border-radius: 20px;
          width: 100%;
          max-width: 340px;
          max-height: 90vh;
          overflow-y: auto;
          padding: 24px;
          text-align: center;
          box-shadow: 0 20px 40px rgba(0,0,0,0.3);
        }
        .payment-modal-header h2 {
          margin: 0 0 8px;
          font-size: 1.25rem;
        }
        .payment-customer-name {
          color: var(--color-text);
          font-weight: 600;
          margin: 0 0 4px;
        }
        .payment-amount {
          color: var(--color-success);
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0 0 16px;
        }
        .payment-amount .original-price-strike {
          text-decoration: line-through;
          color: #9ca3af;
          font-size: 1rem;
          margin-right: 8px;
        }
        .payment-amount .discounted-price-display {
          color: #059669;
        }
        .salt-indicator-modal {
          font-size: 0.85rem;
          color: #3b82f6;
          font-weight: 500;
          margin-left: 4px;
        }

        /* Service Checklist Section in Payment Modal */
        .service-checklist-section {
          margin-bottom: 16px;
          padding: 12px;
          background: #f0fdf4;
          border-radius: 8px;
          border: 1px solid #bbf7d0;
        }
        .service-checklist-title {
          display: block;
          font-size: 0.85rem;
          font-weight: 600;
          color: #166534;
          margin-bottom: 10px;
        }
        .service-checklist-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 8px;
        }
        .service-checkbox-label {
          display: flex;
          align-items: center;
          gap: 6px;
          cursor: pointer;
          font-size: 0.9rem;
          padding: 6px 8px;
          background: white;
          border-radius: 6px;
          border: 1px solid #d1d5db;
          transition: all 0.15s ease;
        }
        .service-checkbox-label:has(input:checked) {
          background: #dcfce7;
          border-color: #86efac;
        }
        .service-checkbox-label input[type="checkbox"] {
          width: 16px;
          height: 16px;
          cursor: pointer;
          accent-color: #22c55e;
        }
        .service-checkbox-text {
          color: #374151;
          font-weight: 500;
        }
        .fertilizer-indicator-modal {
          color: #16a34a;
          font-size: 0.75rem;
          font-weight: 600;
        }
        .addons-indicator-modal {
          color: #7c3aed;
          font-size: 0.75rem;
          font-weight: 600;
        }

        /* Service Add-ons Section in Payment Modal */
        .service-addons-section {
          margin-bottom: 16px;
          padding: 12px;
          background: #f5f3ff;
          border-radius: 8px;
          border: 1px solid #ddd6fe;
        }
        .service-addons-title {
          display: block;
          font-size: 0.85rem;
          font-weight: 600;
          color: #5b21b6;
          margin-bottom: 10px;
        }
        .service-addons-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .service-addon-item {
          display: flex;
          align-items: center;
          gap: 10px;
          cursor: pointer;
          font-size: 0.9rem;
          padding: 8px 12px;
          background: white;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
          transition: all 0.15s ease;
        }
        .service-addon-item:has(input:checked) {
          background: #ede9fe;
          border-color: #c4b5fd;
        }
        .service-addon-item input[type="checkbox"] {
          width: 16px;
          height: 16px;
          cursor: pointer;
          accent-color: #7c3aed;
        }
        .addon-name {
          flex: 1;
          color: #374151;
          font-weight: 500;
        }
        .addon-price {
          color: #7c3aed;
          font-weight: 600;
          font-size: 0.85rem;
        }
        .addons-total {
          margin-top: 10px;
          padding-top: 10px;
          border-top: 1px solid #ddd6fe;
          text-align: right;
          font-weight: 600;
          color: #5b21b6;
          font-size: 0.9rem;
        }

        /* Adjustments Section in Payment Modal (Discount + Extra Charge) */
        .adjustments-section {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 16px;
          padding-bottom: 16px;
          border-bottom: 1px solid var(--color-border);
        }
        .adjustment-item {
          width: 100%;
        }
        .add-adjustment-btn {
          background: none;
          border: 1px dashed var(--color-border);
          border-radius: 8px;
          padding: 10px 12px;
          color: var(--color-text-light);
          cursor: pointer;
          font-size: 0.85rem;
          width: 100%;
          text-align: center;
        }
        .add-adjustment-btn:hover {
          border-color: var(--color-primary);
          color: var(--color-primary);
        }
        .add-extra-charge-btn:hover {
          border-color: #22c55e;
          color: #22c55e;
        }
        .adjustment-form {
          background: var(--color-background-alt);
          border-radius: 8px;
          padding: 10px;
          border: 1px solid var(--color-border);
        }
        .adjustment-form-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 8px;
        }
        .adjustment-label {
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--color-text);
        }
        .remove-adjustment-btn {
          background: none;
          border: none;
          color: #9ca3af;
          font-size: 1rem;
          cursor: pointer;
          padding: 0 4px;
          line-height: 1;
        }
        .remove-adjustment-btn:hover {
          color: #dc2626;
        }
        .discount-form-row {
          display: flex;
          gap: 8px;
          margin-bottom: 8px;
        }
        .discount-type-select {
          flex: 1;
          padding: 8px;
          border: 1px solid var(--color-border);
          border-radius: 6px;
          font-size: 0.85rem;
        }
        .discount-amount-input {
          flex: 1;
          padding: 8px;
          border: 1px solid var(--color-border);
          border-radius: 6px;
          font-size: 0.85rem;
        }
        .adjustment-reason-input {
          width: 100%;
          padding: 8px;
          border: 1px solid var(--color-border);
          border-radius: 6px;
          font-size: 0.85rem;
          margin-bottom: 8px;
          box-sizing: border-box;
        }
        .discount-ongoing-label {
          display: flex;
          align-items: center;
          gap: 10px;
          font-size: 0.85rem;
          color: var(--color-text-light);
          cursor: pointer;
          padding: 6px 0;
          margin-top: 6px;
          background: transparent;
          border: none;
        }
        .discount-ongoing-label:hover {
          color: var(--color-text);
        }
        .discount-ongoing-label input[type="checkbox"] {
          width: 18px;
          height: 18px;
          accent-color: var(--color-primary);
          cursor: pointer;
          flex-shrink: 0;
          margin: 0;
        }
        .discount-ongoing-label span {
          user-select: none;
        }
        .remove-discount-link {
          background: none;
          border: none;
          color: #dc2626;
          font-size: 0.8rem;
          cursor: pointer;
          padding: 4px 0;
          text-decoration: underline;
        }
        .remove-discount-link:hover {
          color: #b91c1c;
        }
        .extra-charge-form-row {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-bottom: 8px;
        }
        .extra-charge-prefix {
          font-size: 0.9rem;
          font-weight: 600;
          color: var(--color-text);
        }
        .extra-charge-amount-input {
          flex: 1;
          padding: 8px;
          border: 1px solid var(--color-border);
          border-radius: 6px;
          font-size: 0.85rem;
        }
        .extra-charge-indicator-modal {
          font-size: 0.75rem;
          color: #22c55e;
          margin-left: 4px;
        }

        /* Photo Upload Section */
        .photo-upload-section {
          border-top: 1px solid var(--color-border);
          padding-top: 12px;
          margin-top: 8px;
        }
        .photo-upload-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 10px;
        }
        .photo-section-label {
          font-size: 0.9rem;
          font-weight: 500;
          color: var(--color-text);
        }
        .add-photo-btn {
          background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%);
          color: white;
          border: none;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 0.8rem;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .add-photo-btn:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(37, 99, 235, 0.3);
        }
        .add-photo-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .photo-thumbnails {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .photo-thumbnail-container {
          position: relative;
          width: 60px;
          height: 60px;
        }
        .photo-thumbnail {
          width: 100%;
          height: 100%;
          object-fit: cover;
          border-radius: 6px;
          cursor: pointer;
          border: 1px solid var(--color-border);
        }
        .photo-thumbnail:hover {
          border-color: var(--color-primary);
        }
        .delete-photo-btn {
          position: absolute;
          top: -6px;
          right: -6px;
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background: #dc2626;
          color: white;
          border: none;
          font-size: 10px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          line-height: 1;
        }
        .delete-photo-btn:hover {
          background: #b91c1c;
        }

        /* Photo Preview Modal */
        .photo-preview-overlay {
          background: rgba(0, 0, 0, 0.9);
        }
        .photo-preview-modal {
          position: relative;
          max-width: 90vw;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        .photo-preview-close {
          position: absolute;
          top: -40px;
          right: 0;
          background: transparent;
          border: none;
          color: white;
          font-size: 24px;
          cursor: pointer;
          padding: 8px;
        }
        .photo-preview-image {
          max-width: 100%;
          max-height: 80vh;
          object-fit: contain;
          border-radius: 8px;
        }
        .photo-preview-caption {
          color: white;
          margin-top: 12px;
          font-size: 0.9rem;
        }

        .payment-options {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        .payment-option-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 20px 16px;
          border: 2px solid var(--color-border);
          border-radius: 12px;
          background: var(--color-background);
          cursor: pointer;
          font-size: 0.9rem;
          font-weight: 600;
          transition: all 0.15s ease;
        }
        .payment-option-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
        }
        .payment-option-btn .payment-icon {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.25rem;
          font-weight: 700;
        }
        /* Square (blue - Square brand color) */
        .payment-option-btn.square {
          border-color: #006aff;
          color: #006aff;
        }
        .payment-option-btn.square .payment-icon {
          background: rgba(0, 106, 255, 0.1);
        }
        .payment-option-btn.square:hover {
          background: rgba(0, 106, 255, 0.05);
        }
        /* Venmo - Paid (green) */
        .payment-option-btn.venmo-paid {
          border-color: #22c55e;
          color: #22c55e;
        }
        .payment-option-btn.venmo-paid .payment-icon {
          background: rgba(34, 197, 94, 0.1);
        }
        .payment-option-btn.venmo-paid:hover {
          background: rgba(34, 197, 94, 0.05);
        }
        /* Venmo - Not Paid (orange/amber) */
        .payment-option-btn.venmo-not-paid {
          border-color: #f59e0b;
          color: #d97706;
        }
        .payment-option-btn.venmo-not-paid .payment-icon {
          background: rgba(245, 158, 11, 0.1);
        }
        .payment-option-btn.venmo-not-paid:hover {
          background: rgba(245, 158, 11, 0.05);
        }
        /* Cash App - Paid (green) */
        .payment-option-btn.cashapp-paid {
          border-color: #22c55e;
          color: #22c55e;
        }
        .payment-option-btn.cashapp-paid .payment-icon {
          background: rgba(34, 197, 94, 0.1);
        }
        .payment-option-btn.cashapp-paid:hover {
          background: rgba(34, 197, 94, 0.05);
        }
        /* Cash App - Not Paid (orange/amber) */
        .payment-option-btn.cashapp-not-paid {
          border-color: #f59e0b;
          color: #d97706;
        }
        .payment-option-btn.cashapp-not-paid .payment-icon {
          background: rgba(245, 158, 11, 0.1);
        }
        .payment-option-btn.cashapp-not-paid:hover {
          background: rgba(245, 158, 11, 0.05);
        }
        /* Cash - Paid (green) */
        .payment-option-btn.cash-paid {
          border-color: #22c55e;
          color: #22c55e;
        }
        .payment-option-btn.cash-paid .payment-icon {
          background: rgba(34, 197, 94, 0.1);
        }
        .payment-option-btn.cash-paid:hover {
          background: rgba(34, 197, 94, 0.05);
        }
        /* Not Paid (gray) */
        .payment-option-btn.not-paid {
          border-color: #9ca3af;
          color: #6b7280;
        }
        .payment-option-btn.not-paid .payment-icon {
          background: rgba(156, 163, 175, 0.1);
        }
        .payment-option-btn.not-paid:hover {
          background: rgba(156, 163, 175, 0.05);
        }

        /* Comp Submit Button (green) */
        .comp-submit-section {
          display: flex;
          justify-content: center;
          padding: 10px 0;
        }
        .payment-option-btn.comp-submit-btn {
          border-color: #22c55e;
          color: #15803d;
          background: rgba(34, 197, 94, 0.1);
          min-width: 200px;
          font-weight: 600;
        }
        .payment-option-btn.comp-submit-btn .payment-icon {
          background: rgba(34, 197, 94, 0.2);
          color: #15803d;
        }
        .payment-option-btn.comp-submit-btn:hover {
          background: rgba(34, 197, 94, 0.2);
          border-color: #16a34a;
        }

        /* Save Adjustments Button */
        .save-adjustments-section {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 15px 0;
          border-bottom: 1px solid #333;
          margin-bottom: 10px;
        }
        .save-adjustments-btn {
          background: linear-gradient(135deg, #3b82f6, #2563eb);
          color: white;
          border: none;
          padding: 12px 30px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          min-width: 180px;
        }
        .save-adjustments-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #2563eb, #1d4ed8);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
        }
        .save-adjustments-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .save-adjustments-hint {
          color: #9ca3af;
          font-size: 12px;
          margin-top: 8px;
          text-align: center;
        }

        /* Zelle - Paid (purple) */
        .payment-option-btn.zelle-paid {
          border-color: #7c3aed;
          color: #7c3aed;
        }
        .payment-option-btn.zelle-paid .payment-icon {
          background: rgba(124, 58, 237, 0.1);
        }
        .payment-option-btn.zelle-paid:hover {
          background: rgba(124, 58, 237, 0.05);
        }

        /* Zelle - Not Paid (orange/amber) */
        .payment-option-btn.zelle-not-paid {
          border-color: #f59e0b;
          color: #d97706;
        }
        .payment-option-btn.zelle-not-paid .payment-icon {
          background: rgba(245, 158, 11, 0.1);
        }
        .payment-option-btn.zelle-not-paid:hover {
          background: rgba(245, 158, 11, 0.05);
        }

        /* Check - Paid (teal) */
        .payment-option-btn.check-paid {
          border-color: #0d9488;
          color: #0d9488;
        }
        .payment-option-btn.check-paid .payment-icon {
          background: rgba(13, 148, 136, 0.1);
        }
        .payment-option-btn.check-paid:hover {
          background: rgba(13, 148, 136, 0.05);
        }

        /* Check - Not Paid (orange/amber) */
        .payment-option-btn.check-not-paid {
          border-color: #f59e0b;
          color: #d97706;
        }
        .payment-option-btn.check-not-paid .payment-icon {
          background: rgba(245, 158, 11, 0.1);
        }
        .payment-option-btn.check-not-paid:hover {
          background: rgba(245, 158, 11, 0.05);
        }

        /* Payment Section Divider */
        .payment-section-divider {
          display: flex;
          align-items: center;
          margin: 16px 0 12px 0;
          color: #6b7280;
          font-size: 0.85rem;
          font-weight: 500;
        }
        .payment-section-divider::before,
        .payment-section-divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: #e5e7eb;
        }
        .payment-section-divider span {
          padding: 0 12px;
        }

        /* Invoice Payment Options */
        .invoice-payment-options {
          display: flex;
          justify-content: center;
          gap: 12px;
        }
        .invoice-payment-options .payment-option-btn {
          flex: 0 0 auto;
          min-width: 120px;
        }

        /* Payment Badge */
        .payment-badge {
          font-size: 0.7rem;
          padding: 3px 8px;
          border-radius: 10px;
          font-weight: 600;
          white-space: nowrap;
          margin-left: auto;
          flex-shrink: 0;
        }
        .payment-badge.paid {
          background: rgba(34, 197, 94, 0.15);
          color: #15803d;
        }
        .payment-badge.invoice-sent {
          background: rgba(234, 179, 8, 0.15);
          color: #a16207;
        }
        .payment-badge.not-paid {
          background: rgba(156, 163, 175, 0.15);
          color: #6b7280;
        }
        .payment-badge.skipped {
          background: rgba(156, 163, 175, 0.15);
          color: #6b7280;
        }

        /* Square Badge States */
        .square-badge {
          display: inline-block;
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 0.7rem;
          font-weight: 600;
          margin-top: 4px;
        }
        .square-badge.paid {
          background: rgba(16, 185, 129, 0.15);
          color: #059669;
        }
        .square-badge.invoice-sent {
          background: rgba(139, 92, 246, 0.15);
          color: #7c3aed;
        }
        .square-badge.pending {
          background: rgba(107, 114, 128, 0.15);
          color: #6b7280;
        }

        /* Billing Modal */
        .billing-modal {
          background: white;
          border-radius: 16px;
          width: 90%;
          max-width: 480px;
          max-height: 85vh;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .billing-modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid var(--color-border);
        }
        .billing-modal-header h2 {
          margin: 0;
          font-size: 1.25rem;
          font-weight: 600;
        }
        .billing-modal-content {
          padding: 16px 20px;
          overflow-y: auto;
          flex: 1;
        }
        .billing-modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 20px;
          border-top: 1px solid var(--color-border);
        }
        .billing-section {
          margin-bottom: 20px;
        }
        .billing-section:last-child {
          margin-bottom: 0;
        }
        .billing-section-title {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.9rem;
          font-weight: 600;
          margin-bottom: 12px;
          color: var(--color-text);
        }
        .billing-section-title.warning {
          color: #d97706;
        }
        .billing-section-title.manual {
          color: #6b7280;
        }
        .billing-icon {
          font-size: 1rem;
        }
        .billing-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          background: #f9fafb;
          border-radius: 8px;
          margin-bottom: 8px;
          font-size: 0.9rem;
        }
        .billing-item:last-child {
          margin-bottom: 0;
        }
        .billing-item.auto-bill {
          background: rgba(16, 185, 129, 0.08);
        }
        .billing-item.no-email {
          background: rgba(245, 158, 11, 0.08);
        }
        .billing-item.manual {
          background: rgba(107, 114, 128, 0.08);
        }
        .billing-check {
          color: #10b981;
          font-size: 1rem;
        }
        .billing-warning {
          color: #f59e0b;
          font-size: 1rem;
        }
        .billing-manual {
          color: #6b7280;
          font-size: 0.9rem;
        }
        .billing-name {
          flex: 1;
          font-weight: 500;
        }
        .billing-amount {
          font-weight: 600;
          color: var(--color-text);
        }
        .billing-method {
          font-size: 0.8rem;
          color: var(--color-text-light);
          background: rgba(0,0,0,0.05);
          padding: 2px 8px;
          border-radius: 4px;
        }
        .no-billable {
          text-align: center;
          color: var(--color-text-light);
          padding: 24px;
        }

        /* Billing Results */
        .billing-results {
          padding: 8px 0;
        }
        .billing-results-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
          padding-bottom: 12px;
          border-bottom: 1px solid #e5e7eb;
        }
        .billing-results-header h3 {
          font-size: 1rem;
          font-weight: 600;
          color: #374151;
          margin: 0;
        }
        .results-summary {
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 0.75rem;
          font-weight: 600;
        }
        .results-summary.success {
          background: rgba(16, 185, 129, 0.15);
          color: #059669;
        }
        .results-summary.failed {
          background: rgba(239, 68, 68, 0.15);
          color: #dc2626;
        }
        .billing-results-list {
          max-height: 300px;
          overflow-y: auto;
        }
        .billing-result-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px;
          background: #f9fafb;
          border-radius: 8px;
          margin-bottom: 8px;
          font-size: 0.85rem;
        }
        .billing-result-item.success {
          border-left: 3px solid #10b981;
        }
        .billing-result-item.failed {
          border-left: 3px solid #ef4444;
        }
        .billing-result-item .result-icon {
          font-weight: bold;
          font-size: 1rem;
        }
        .billing-result-item.success .result-icon {
          color: #10b981;
        }
        .billing-result-item.failed .result-icon {
          color: #ef4444;
        }
        .result-name {
          font-weight: 500;
          color: #374151;
        }
        .result-amount {
          margin-left: auto;
          font-weight: 600;
          color: #10b981;
        }
        .result-status {
          font-size: 0.75rem;
          color: #6b7280;
        }
        .result-reason {
          margin-left: auto;
          font-size: 0.8rem;
          color: #ef4444;
        }
        .no-results {
          text-align: center;
          color: var(--color-text-light);
          padding: 24px;
        }
        .done-btn {
          width: 100%;
        }
      `}</style>
    </div>
  );
}

export default ServiceMode;
